import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useNavBack } from "../hooks/useNavBack";
import { useFocusFromURL } from "../hooks/useFocusFromURL";
// Vincent P1.8 (Eric 2026-07-31 SPEC STRICK PROD) — Hook custom pour le
// split-pane (state + drag listeners + persist).
import { useVideoSplitPane } from "../hooks/useVideoSplitPane";
// Vincent P1.8.b (Eric 2026-07-31 SPEC STRICK PROD) — Hook custom pour le
// deep-linking URL (?t / ?tab / ?source / ?focus) + auto-switch sous-onglet
// + auto-open KnowledgeDetailModal (B613 + B621).
import { useVideoDeepLink } from "../hooks/useVideoDeepLink";
import { cachedGet } from "../lib/apiCache";
// Vincent P1.8.c (Eric 2026-07-31 SPEC STRICK PROD) — Hook custom pour le
// fetch principal de la vidéo (`GET /api/videos/:id`) + états `data`/`loading`.
import { useVideoData } from "../hooks/useVideoData";
import { libelleTypeFichier } from "../lib/typeFichier"; // Tâche 1465
// Vincent P1.8.d (Eric 2026-07-31 SPEC STRICK PROD) — Hook custom pour les
// actions vidéo — édition des métadonnées (titre/desc/thème/dropbox/
// catégorie/parcours) via startEdit + saveEdit. Rerun-whisper et
// delete-video vivent dans WhisperToolbar.js et VideoDetailModals.js.
import { useVideoActions } from "../hooks/useVideoActions";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog, promptDialog } from "../lib/uiDialogs";
// Vincent 2026-05-06 (bug #64) — affichage du markdown Notebook procédure
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "../components/ui/button";
import {
  ArrowLeft,
  Clock,
  FileText,
  VideoCamera,
  CheckCircle,
  Question,
  ListChecks,
  ShieldWarning,
  Lightbulb,
  CircleNotch,
  ArrowClockwise,
  Waveform,
  MagnifyingGlass,
  Warning,
  Play,
  Pause,
  SpeakerHigh,
  SpeakerSlash,
  CornersOut,
  PictureInPicture,
  Gauge,
  SkipBack,
  SkipForward,
  X,
  CaretDown,
  DownloadSimple,
  UploadSimple,
  ShareNetwork,
  Copy,
  Check,
  LinkSimple,
  Trash,
  PencilSimple,
  FloppyDisk,
  XCircle,
  ClockCounterClockwise,
} from "@phosphor-icons/react";
import { useLang } from "../contexts/LanguageContext";
import { canEditContent, canAccessAdmin } from "../utils/permissions";
import { useAuth } from "../contexts/AuthContext";
import PromoteVideoButton from "../components/PromoteVideoButton";
import KeyboardShortcutsModal from "../components/video/KeyboardShortcutsModal";
import ReportMarkdownModal from "../components/video/ReportMarkdownModal";
import TranscriptHistoryModal from "../components/video/TranscriptHistoryModal";
import WhisperToolbar from "../components/video/WhisperToolbar";
import useShareLinks from "../hooks/useShareLinks";
import useTranscripts from "../hooks/useTranscripts";
import useStreamToken from "../hooks/useStreamToken";
// Vincent B683 (Eric 2026-02-XX) — Wrapper Transcript (Search + Selector + 3 SubTabs)
import TranscriptPanel from "../components/video/TranscriptPanel";
import SpeakersPanel from "../components/video/SpeakersPanel";
import SynthesisPanel from "../components/video/SynthesisPanel";
import ScriptsPanel from "../components/video/ScriptsPanel";
import ProceduresPanel from "../components/video/ProceduresPanel";
import QAPanel from "../components/video/QAPanel";
import KnowledgeCleanupModal from "../components/video/KnowledgeCleanupModal";  // Tâche 1283
import VideoPlayerArea from "../components/video/VideoPlayerArea";
// Tâche 1435, étape 2 — Carte « Source web » + mise à jour manuelle d'une URL.
import SourceWebCard from "../components/text/SourceWebCard";
import VideoDetailModals from "../components/video/VideoDetailModals";
// Eric 2026-07-20 (iter162) — Extractions refactor VideoDetailPage
import AIJobProgressBar from "../components/video/AIJobProgressBar";
import { VideoLoadingState, VideoNotFoundState } from "../components/video/VideoStateBanners";
import PanelTabsBar from "../components/video/PanelTabsBar";
import FavoritesTabPanel from "../components/video/FavoritesTabPanel";
import useSpeakerReprocess from "../hooks/useSpeakerReprocess";
import usePipelineStatus from "../hooks/usePipelineStatus";
import useTranscriptGeneration from "../hooks/useTranscriptGeneration";
import VideoDetailHeader from "../components/video/VideoDetailHeader";
import TagSuggestionsBar from "../components/video/TagSuggestionsBar";
import TagsManagerDialog from "../components/video/TagsManagerDialog";
import VideoPipelineStatus from "../components/video/VideoPipelineStatus";
import KnowledgeExtractionProgress from "../components/video/KnowledgeExtractionProgress";
import KnowledgeTab from "../components/video/KnowledgeTab";
import KUFavoriteHeart from "../components/video/KUFavoriteHeart";
import { useFavoriteKuIds } from "../hooks/useFavoriteKuIds";
import ImportKnowledgeModal from "../components/ImportKnowledgeModal";
import JargonTermsPanel from "../components/video/JargonTermsPanel";
import VideoActionsCards from "../components/video/VideoActionsCards"; // Vincent B890 — cards toujours visibles sous la vidéo
import { parseTextTranscriptToSegments } from "../utils/transcriptUtils";
import ScriptsPage from "./ScriptsPage";
import { KnowledgeDetailModal } from "../components/knowledge/KnowledgeDetailModal";
import { formatTitleHistoryEntry } from "../lib/knowledgeFormat";

import { API_BASE as API } from "../lib/apiBase";

// Eric (2026-06-12) — Polling des jobs d'arrière-plan (générations IA longues).
// Cloudflare (proxy de knoria.com en production) coupe les requêtes > ~100 s :
// les générations partent donc en job backend et on polle le statut ici.
// Eric (2026-06-14) — Ajout d'un callback `onProgress` pour alimenter la barre
// de progression (champ chunks_processed / total_chunks / current_step publiés
// par routers/agent.py update_bg_job_progress).
async function pollAgentJob(jobId, { intervalMs = 2500, maxMs = 900000, onProgress = null } = {}) {
  const t0 = Date.now();
  while (Date.now() - t0 < maxMs) {
    await new Promise((r) => setTimeout(r, intervalMs));
    const { data } = await axios.get(`${API}/api/agent/jobs/${jobId}`, { withCredentials: true });
    if (typeof onProgress === "function") {
      try {
        onProgress({
          status: data.status,
          chunks_processed: data.chunks_processed || 0,
          total_chunks: data.total_chunks || 0,
          current_step: data.current_step || "running",
        });
      } catch (_) { /* swallow */ }
    }
    if (data.status === "done") return data.result || {};
    if (data.status === "error") throw new Error(data.error || "La génération a échoué");
  }
  throw new Error("Délai dépassé (15 min) — la génération continue peut-être côté serveur, rechargez la page dans quelques minutes.");
}
import { downloadReportPDF, downloadReportMarkdown } from "../lib/videoReportHelpers";
// Vincent B934 (Eric 2026-02-14 SPEC STRICK PROD) — Dédoublonnage des
// locuteurs entre les 3 sources (Whisper, Fireflies, Fusionnée).
import { normalizeSpeakerKey } from "../lib/speakerNormalize";
// Vincent (2026-02) — Refonte : persistance édition vidéo centralisée
// (Catégorie + diff Parcours). Partagée avec FileExplorerPage.
// Vincent P1.8.d (Eric 2026-07-31 SPEC STRICK PROD) — Imports déplacés
// dans `hooks/useVideoActions.js` (startEdit + saveEdit y sont désormais).
// Eric (2026-06-15 — iter102) — Helpers extraits dans /lib/videoDetailFormat.js
// (premier lot de la refacto progressive — voir CHANGELOG iter102).
import {
  TYPE_ICONS,
  TYPE_COLORS,
  SPEAKER_COLORS,
  PIPELINE_STAGES,
  SPEED_OPTIONS,
  formatTime,
  formatTimeBracket,
  highlightText,
} from "../lib/videoDetailFormat";

// Bogue 1199 — Traduction du MediaError du navigateur en langage clair.
// C'est LA donnée qui manquait pour diagnostiquer un refus de lecture.
const _mediaErrorLabel = (media) => {
  if (!media) return "Le navigateur n'a donné aucun code d'erreur : la vidéo a probablement mis trop de temps à répondre.";
  const codes = {
    1: "Lecture interrompue (code 1) — le chargement a été annulé.",
    2: "Erreur réseau (code 2) — le flux s'est coupé pendant le téléchargement.",
    3: "Erreur de décodage (code 3) — le fichier est lisible mais son contenu ne se décode pas.",
    4: "Format non pris en charge (code 4) — ce codec ou ce conteneur n'est pas lu par ce navigateur.",
  };
  const base = codes[media.code] || `Erreur de lecture (code ${media.code}).`;
  return media.message ? `${base} Détail : ${media.message}` : base;
};

export default function VideoDetailPage() {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const goBack = useNavBack();
  const { user } = useAuth();
  // Vincent #376/Etienne (Eric 2026-06-24 iter114) — `isAdmin` utilise désormais
  // le helper canonique `canEditContent` (single source of truth aligné avec
  // backend `require_editor`). Élimine le risque de drift qui a causé le bug
  // Etienne en iter112. Voir /app/frontend/src/utils/permissions.js.
  const isAdmin = canEditContent(user);
  // Vincent #336/#337 (Eric 2026-06-17 SPEC STRICK) → Eric 2026-06-25 (iter117
  // B2) — migré vers canEditContent (helper canonique, aligné backend
  // require_editor). Whitelist identique : founder/admin/ceo/president/vp/
  // concepteur/employee_advanced + access_level=avance.
  const canEditKnowledge = canEditContent(user);
  // Vincent B725 v2 (Eric 2026-02-13) — La logique `canDownloadDocument`
  // pour vidéo a été RETIRÉE avec le bouton « Télécharger MP4 ». Le
  // téléchargement des vidéos est désormais interdit à tous les utilisateurs.

  // Vincent B3 XLS 2026-08-10 (Eric 2026-02-10) — Navigation Précédent/Suivant
  // à travers la liste des vidéos accessibles à l'utilisateur, à la manière
  // du fait dans KnowledgeDetailModal. La liste est fetchée une fois au
  // montage et re-triée par `created_at` desc (même ordre que
  // FileExplorer / Explorateur de fichiers). Les boutons sont rendus dans
  // VideoDetailHeader, à droite au-dessus d'Importer/Télécharger.
  // Vincent B840 v2 (Eric 2026-02-14 SPEC STRICK PROD) — La dépendance
  // `[videoId]` refetchait la liste à chaque navigation Prev/Next, ce
  // qui provoquait la disparition des boutons pour les non-éditeurs quand
  // la nouvelle réponse tardait ou différait (ordre paginé instable).
  // La liste des vidéos accessibles à l'utilisateur ne dépend PAS du
  // videoId courant — on la fetche une seule fois au mount de la page.
  const [siblingVideos, setSiblingVideos] = useState([]);
  useEffect(() => {
    let cancelled = false;
    // Vincent B916 (Eric 2026-02-14 SPEC STRICK PROD) — Bug PROD : les
    // boutons Prev/Next disparaissaient pour toutes les vidéos au-delà
    // de la 50e (pagination par défaut de `/api/videos`). Sur les orgs
    // avec > 50 vidéos, `_siblingIdx === -1` cachait TOUT le nav.
    // Fix : `?limit=500` (cap serveur) — largement suffisant pour
    // couvrir les orgs Knoria/FLHQ (< 200 vidéos aujourd'hui).
    // B1085 — La liste sert uniquement aux flèches Précédent/Suivant : elle
    // est mise en cache 60 s, donc partagée entre deux vidéos consultées à la
    // suite (531 ms économisés à chaque navigation).
    cachedGet(axios, `${API}/api/videos?limit=500`, { ttl: 60000 })
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : (res.data?.items || res.data?.videos || []);
        // Vincent B718 (Eric 2026-02-11) — Trie les frères selon le même
        // critère que l'Explorateur de fichiers (persisté dans sessionStorage
        // par FileExplorerPage). Défaut = name_asc (A→Z), synchronisé avec
        // la valeur initiale de l'Explorateur.
        let sortBy = "name_asc";
        try { sortBy = sessionStorage.getItem("fileExplorer.sortBy") || "name_asc"; } catch (_) { /* silencieux */ }
        const sorted = [...list];
        if (sortBy === "date_desc") {
          sorted.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        } else if (sortBy === "date_asc") {
          sorted.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
        } else if (sortBy === "name_desc") {
          sorted.sort((a, b) => (b.title || "").localeCompare(a.title || "", "fr", { sensitivity: "base" }));
        } else {
          // name_asc (défaut) + fallbacks (duration_*, category_*) traités
          // comme "name_asc" pour Prev/Next — la catégorie ajoute des groupes
          // sur l'écran Explorateur mais Prev/Next reste flat par nom.
          sorted.sort((a, b) => (a.title || "").localeCompare(b.title || "", "fr", { sensitivity: "base" }));
        }
        setSiblingVideos(sorted);
      })
      .catch(() => { /* silencieux : Prev/Next simplement absents en cas d'erreur */ });
    return () => { cancelled = true; };
    // NB : deps [] volontaire (fetch au mount uniquement). Sinon les boutons
    // Prev/Next disparaissent temporairement à chaque navigation.
  }, []);



  // Vincent P1.8.c (Eric 2026-07-31 SPEC STRICK PROD) — Fetch principal
  // de la vidéo migré dans `useVideoData`. Expose `data`, `setData`,
  // `loading`, `setLoading` et `loadVideoData` (stable useCallback).
  const { data, setData, loading, setLoading, loadVideoData } = useVideoData({ videoId, api: API });
  // ⚠️ Tâches 1468/1469 — ces hooks DOIVENT vivre ici, avant tout retour
  // anticipé de la page : React exige le même ordre d'appel à chaque rendu, et
  // les poser plus bas (après le `return` de chargement) cassait la
  // compilation. `isTextDoc` n'est calculé que bien plus loin, on lit donc
  // directement la donnée.
  const estFichierTexte = data?.video?.is_text_doc === true;
  const [siblingDocs, setSiblingDocs] = useState([]);
  useEffect(() => {
    if (!estFichierTexte) return undefined;
    let annule = false;
    cachedGet(axios, `${API}/api/text-documents`, { ttl: 60000 })
      .then((res) => {
        if (annule) return;
        const liste = res.data?.text_documents || [];
        const tries = [...liste].sort((a, b) => (a.title || "").localeCompare(
          b.title || "", "fr", { sensitivity: "base" }));
        setSiblingDocs(tries);
      })
      .catch(() => { /* silencieux : la navigation est simplement absente */ });
    return () => { annule = true; };
  }, [estFichierTexte]);

  // Tâche 1469 — le réglage d'organisation, et qui a le droit d'y toucher.
  const [politiqueTelechargement, setPolitiqueTelechargement] = useState(
    { bloque: false, peut_regler: false });
  useEffect(() => {
    if (!estFichierTexte) return;
    axios.get(`${API}/api/text-documents-download-policy`, { withCredentials: true })
      .then((r) => setPolitiqueTelechargement(r.data || { bloque: false, peut_regler: false }))
      .catch(() => { /* silencieux : on reste sur « permis », le défaut voulu */ });
  }, [estFichierTexte]);



  const { lang, t, tr } = useLang();
  // Eric (2026-06-14) — Progression visuelle des jobs IA d'arrière-plan
  // (anciennement un toast seul). Remplit `{kind, chunks_processed, total_chunks,
  // step}` pendant le polling, puis se vide en fin de job.
  const [aiJobProgress, setAiJobProgress] = useState(null);

  // Bogue 1218 (Eric 2026-06-26) — La transcription fusionnée « clignotait »
  // pendant la génération des connaissances. Cause : `pollAgentJob` appelle
  // `onProgress` toutes les 2,5 s, et chaque appel créait un OBJET NEUF, donc
  // un nouveau rendu de toute la page — y compris la liste de plusieurs
  // milliers de segments affichée plus bas. Or la plupart des tours
  // n'apportent AUCUNE nouveauté (mêmes chunks, même étape). On renvoie donc
  // l'état précédent à l'identique quand rien n'a changé : React ne rend rien.
  const setJobProgressIfChanged = useCallback((kind, p) => {
    setAiJobProgress((prev) => {
      const next = { kind, ...p };
      if (
        prev
        && prev.kind === next.kind
        && prev.status === next.status
        && prev.chunks_processed === next.chunks_processed
        && prev.total_chunks === next.total_chunks
        && prev.current_step === next.current_step
      ) {
        return prev;   // même référence → aucun rendu
      }
      return next;
    });
  }, []);
  // Vincent bugs #106/#107/#108/#87 (2026-05-08) — 3 sous-onglets uniformes pour Q&R / Scripts / Procédures :
  // **Pipeline** (par défaut, contient le bouton Générer + le rapport) → **Visualiser** (lecture seule) → **Modifier** (édition).
  // L'ancien sous-onglet "Rapport" est fusionné dans "Pipeline" (bouton Rapport à droite du bouton Générer).
  // Vincent B869 (Eric 2026-02-14 SPEC STRICK PROD) — Sous-onglet par
  // défaut = « edit » (renommé « Liste des [XXX] » dans l'UI) au lieu
  // de « pipeline ». Motif Vincent : la liste est bien plus utilisée
  // que le pipeline pour valider les connaissances, donc c'est elle
  // qu'on ouvre en premier. Le sous-onglet Pipeline reste accessible.
  const [qaSubtab, setQaSubtab] = useState("edit");
  // Vincent 2026-05-06 (bug #58) — option de conserver les anciennes Q&R lors d'une relance
  const [qaKeepPrevious, setQaKeepPrevious] = useState(true);
  // Vincent #21 / #23 batch FLHQ (Eric 2026-02-06) — mêmes cases à cocher pour
  // Scripts de vente et Procédures, en miroir du flow Q&R ci-dessus.
  const [scriptsKeepPrevious, setScriptsKeepPrevious] = useState(true);
  const [proceduresKeepPrevious, setProceduresKeepPrevious] = useState(true);
  const [scriptsSubtab, setScriptsSubtab] = useState("edit");
  const [proceduresSubtab, setProceduresSubtab] = useState("edit");
  // Vincent B839 (Eric 2026-02-14 SPEC STRICK PROD) — Sous-onglets Pipeline /
  // Modifier (ou Visualiser pour les non-éditeurs) pour Concepts et Objections,
  // alignés sur QAPanel / ScriptsPanel / ProceduresPanel.
  const [conceptsSubtab, setConceptsSubtab] = useState("edit");
  const [objectionsSubtab, setObjectionsSubtab] = useState("edit");
  // Vincent B848 (Eric 2026-02-14 SPEC STRICK PROD) — Pour les
  // non-éditeurs (canEditKnowledge=false), l'onglet « Pipeline » n'a
  // aucun sens (aucun bouton n'y est visible pour eux), on force donc
  // tous les sous-onglets à « view » dès qu'on sait que l'utilisateur
  // ne peut pas éditer. Le bouton « Pipeline » sera aussi masqué dans
  // chaque panneau (cf. wrappers `{canEditKnowledge && ...}`).
  useEffect(() => {
    if (canEditKnowledge) return;
    if (qaSubtab === "pipeline") setQaSubtab("view");
    if (scriptsSubtab === "pipeline") setScriptsSubtab("view");
    if (proceduresSubtab === "pipeline") setProceduresSubtab("view");
    if (conceptsSubtab === "pipeline") setConceptsSubtab("view");
    if (objectionsSubtab === "pipeline") setObjectionsSubtab("view");
  }, [canEditKnowledge]);
  // Vincent bug #75 (2026-05-08) — KU ouvert dans le modal "comme base de
  // connaissance". Déclaré ici (au lieu de plus bas) pour être passé au hook
  // useVideoDeepLink (P1.8.b) — le hook a besoin de `setDetailModalKu` pour
  // ouvrir auto le modal sur `?focus=<ku_id>` (B621).
  const [detailModalKu, setDetailModalKu] = useState(null);
  // Eric (2026-02-10) — Filet Prev/Next Scripts : Scripts n'étant pas dans
  // `knowledge_units` (collection `sales_scripts` séparée), on maintient
  // une liste dédiée + son fetch trigger via `ScriptsPage embedded`.
  // Cette liste sert à calculer les siblings Prev/Next quand le
  // KnowledgeDetailModal est ouvert sur un script (voir `detailModalNav`).
  const [scriptsListForModal, setScriptsListForModal] = useState([]);
  // Eric (2026-02-10 — Vague 1 P2) — Tick incrémental pour forcer
  // `ScriptsPage embedded` à re-fetch sa liste après une édition depuis
  // la modale partagée (title / turns v2/v3 / status). Sans cela, la carte
  // afficherait toujours l'ancien titre/status jusqu'à un reload manuel.
  const [scriptsReloadTick, setScriptsReloadTick] = useState(0);

  // Vincent P1.8.b (Eric 2026-07-31 SPEC STRICK PROD) — Deep-linking URL :
  // parse `?t / ?tab / ?source / ?focus` + effets B613 (auto-switch sous-onglet
  // « edit » sur focus éditeur) + B621 (auto-open KnowledgeDetailModal).
  const { startTime, urlTab, urlSource, initialActivePanel } = useVideoDeepLink({
    canEditKnowledge,
    setQaSubtab,
    setScriptsSubtab,
    setProceduresSubtab,
    knowledgeUnits: data?.knowledge_units,
    setDetailModalKu,
  });

  const [activePanel, setActivePanel] = useState(initialActivePanel);
  // Vincent B590.b (Eric 2026-07-29 SPEC STRICK) — Auto-scroll + highlight
  // sur `?focus=<item_id>` après ouverture du bon panel. Les items concernés
  // (objections, concepts, Q&R, scripts, procédures) exposent `data-focus-target`.
  useFocusFromURL([activePanel, videoId]);

  // Eric (2026-06-14 — LOT H) — Stub de dev/QA : ?fake_job=qa|concepts simule
  // une progression locale (4s) sans appel backend. Utile pour valider la
  // barre de progression sans lancer un vrai pipeline 3 min.
  useEffect(() => {
    const fk = new URLSearchParams(window.location.search).get("fake_job");
    if (fk !== "qa" && fk !== "concepts") return;
    const TOTAL = 8;
    let i = 0;
    setAiJobProgress({ kind: fk, chunks_processed: 0, total_chunks: TOTAL, current_step: "starting" });
    const it = setInterval(() => {
      i += 1;
      if (i >= TOTAL) {
        clearInterval(it);
        setAiJobProgress({ kind: fk, chunks_processed: TOTAL, total_chunks: TOTAL, current_step: "finalizing" });
        setTimeout(() => setAiJobProgress(null), 1200);
      } else {
        setAiJobProgress({ kind: fk, chunks_processed: i, total_chunks: TOTAL, current_step: `created_${i}` });
      }
    }, 500);
    return () => clearInterval(it);
  }, []);

  // Vincent #336 (Eric 2026-06-17 SPEC STRICK) — Force le sous-onglet par défaut
  // selon les droits d'édition :
  //   - Éditeurs (canEditKnowledge) : voient "Pipeline" + "Modifier" (par défaut Pipeline)
  //   - Non-éditeurs : voient UNIQUEMENT "Visualiser" (forcé)
  useEffect(() => {
    if (!canEditKnowledge) {
      setQaSubtab("view");
      setScriptsSubtab("view");
      setProceduresSubtab("view");
    } else {
      // Pour les éditeurs, on évite l'état "view" qui n'est plus visible
      setQaSubtab((s) => (s === "view" ? "pipeline" : s));
      setScriptsSubtab((s) => (s === "view" ? "pipeline" : s));
      setProceduresSubtab((s) => (s === "view" ? "pipeline" : s));
    }
  }, [canEditKnowledge]);
  // Vincent #97 (2026-05-12) — Séparateur draggable entre vidéo (gauche) et
  // panneau onglets (droite) sur viewport ≥ lg. La position est persistée dans
  // localStorage pour que chaque admin garde sa préférence. Sur < lg : stack
  // vertical (drag horizontal n'a aucun sens sur mobile).
  // Vincent (Eric 2026-07-19) — Splitter horizontal entre lecteur vidéo (gauche) &
  // panneau onglets (droite) sur viewport ≥ lg. La position est persistée dans
  // localStorage pour que chaque admin garde sa préférence. Sur < lg : stack
  // vertical (drag horizontal n'a aucun sens sur mobile).
  // Vincent P1.8 (Eric 2026-07-31 SPEC STRICK PROD) — Toute la logique
  // (state, drag listeners, persist, isStacked, helpers) a été extraite dans
  // le hook `useVideoSplitPane` (voir `/hooks/useVideoSplitPane.js`) pour
  // dégonfler VideoDetailPage. Comportement strictement identique : mêmes
  // noms de variables retournés → aucun changement de JSX/handler.
  const {
    splitPct,
    setSplitPct,
    setSplitPctPersistent,
    isDragging,
    setIsDragging,
    isStacked,
    containerRef,
    toggleStacked,
    resetSplit,
  } = useVideoSplitPane();
  // Vincent bug #27 (2026-05-08) — Modal pour visualiser le rapport en markdown
  const [reportModal, setReportModal] = useState(null); // {type: 'qa'|'scripts'|'procedures'|'fusion', title, markdown}
  // Eric / Vincent (2026-05-13) — État spinner pour les boutons "Tout accepter mineures" / "Tout retirer".
  const [bulkMinorBusy, setBulkMinorBusy] = useState(false);
  // Vincent 2026-05-06 (bug #64) — Notebook procedure document state
  const [notebookProcedureDoc, setNotebookProcedureDoc] = useState(null);
  const [notebookProcRunning, setNotebookProcRunning] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(true);
  const [videoError, setVideoError] = useState(null);
  // Bogue 1199 (Éric 2026-06-25) — Cause exacte du refus de lecture
  // (MediaError), affichée à l'écran : sans elle, « Lecture impossible » ne
  // dit ni pourquoi ni quoi faire, et je ne peux rien diagnostiquer à distance.
  const [videoErrorDetail, setVideoErrorDetail] = useState(null);
  const videoRecoveryTriedRef = useRef(false);
  // Vincent (2026-07-22) — P1-4 Phase 3 refactor : usePipelineStatus hook
  // gère le polling, l'auto-cleanup et le monotonic-progress-clamp.
  // pipelineStatus / setPipelineStatus / startPolling sont exposés pour
  // rester rétro-compatibles avec les callsites existants.
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [transcriptSearch, setTranscriptSearch] = useState("");
  const [controlsVisible, setControlsVisible] = useState(true);
  const [isSeeking, setIsSeeking] = useState(false);
  const [hoverTime, setHoverTime] = useState(null);
  const [hoverX, setHoverX] = useState(0);
  // Vincent B680 (Eric 2026-02-XX) — 5 useState + 5 handlers déplacés dans
  // hooks/useShareLinks.js (-55 lignes de VideoDetailPage.js).
  const {
    showShareModal,
    setShowShareModal,
    shareLinks,
    shareLoading,
    shareCopied,
    shareExpiry,
    setShareExpiry,
    openShareModal,
    createShareLink,
    copyShareLink,
    revokeShareLink,
  } = useShareLinks({ videoId, api: API, lang });
  const [speakerData, setSpeakerData] = useState(null);
  const [speakerMapping, setSpeakerMapping] = useState({});
  const [speakerSaving, setSpeakerSaving] = useState(false);
  const [showCrossValidate, setShowCrossValidate] = useState(false);
  // Vincent B681 (Eric 2026-02-XX) — crossValidate* + transcriptSubTab +
  // pollMergedTranscript migrés dans hooks/useTranscripts.js (-40 lignes).
  // useTranscripts est instancié plus bas (a besoin de setAllTranscripts du
  // useTranscriptGeneration hook).
  const [rejectedCorrections, setRejectedCorrections] = useState([]);
  // Vincent (2026-05-12) — Édition inline d'une correction proposée
  // (5e colonne "✎ pen") : {cKey: string, draft: string} | null.
  const [editingCorrection, setEditingCorrection] = useState(null);
  // URL validation (Vincent 2026-05-01) — scan transcript for spoken URLs
  const [urlValidating, setUrlValidating] = useState(false);
  const [urlProposals, setUrlProposals] = useState(null);
  // Eric 2026-05-05 — auto-ponctuation (remplace Fireflies)
  const [punctuationLoading, setPunctuationLoading] = useState(false);
  const [punctuationProposal, setPunctuationProposal] = useState(null);
  const [punctuationApplying, setPunctuationApplying] = useState(false);
  // Vincent bug #74 (2026-05-07) — Index → bool, true = mark refusée par l'utilisateur.
  // Reset à chaque nouvelle proposition de ponctuation.
  const [rejectedPunctMarks, setRejectedPunctMarks] = useState({});
  // Vincent bug #74 (2026-05-08) — état explicite par marque de ponctuation : 'proposed' (bordeaux par défaut) | 'accepted' (bleu) | 'rejected' (gris barré).
  // L'utilisateur peut cliquer ✓ pour accepter explicitement (passe en bleu) ou ✗ pour refuser (passe en gris barré).
  const [acceptedPunctMarks, setAcceptedPunctMarks] = useState({});
  const [urlApplying, setUrlApplying] = useState(null);
  // Vincent bug #18 (2026-05-07) — track which suggestion is selected per URL proposal dropdown.
  const [urlDropdownSelections, setUrlDropdownSelections] = useState({});
  // Vincent bug #75 (2026-05-07) — inline Q&R editing in the video tab.
  const [editingInlineKuId, setEditingInlineKuId] = useState(null);
  // Vincent #139 (Eric 2026-05-13) — Inline AI title generator for Q&R :
  // propositions IA + historique cliquable + bouton effacer historique avec
  // modal de confirmation propre (pas le confirm() natif).
  const [qaTitleAi, setQaTitleAi] = useState({ kuId: null, generating: false, proposals: [], history: [] });
  // Vincent #110 (Eric 2026-05-15) — État d'ouverture du dropdown historique
  // de titres par Q&R. Permet a Vincent de toggler l'affichage de l'historique
  // au lieu de toujours l'avoir ouvert quand history.length > 0.
  const [qaHistoryOpenForKuId, setQaHistoryOpenForKuId] = useState(null);
  const [qaClearHistoryConfirm, setQaClearHistoryConfirm] = useState(null); // ku object pending clear

  // Vincent bug #75 (2026-05-08) — KU ouvert dans le modal "comme base de
  // connaissance". Déclaré plus haut (P1.8.b) pour être passé au hook
  // useVideoDeepLink.
  // Vincent XLSX 2026-07-09 15h20 Row #8 (Eric 2026-07-09 SPEC STRICK) —
  // Optimistic status pour les boutons Accepter/Refuser des Objections
  // (parité avec Q&R/Concepts/Procédures).
  const [objectionOptStatus, setObjectionOptStatus] = useState({});
  // Vincent B614 (Eric 2026-07-30 SPEC STRICK) — Set des KUs favorites de
  // l'utilisateur pour afficher le ♥ sur chaque Objection/Réfutation (parité
  // avec Q&R, Scripts, Procédures, Concepts). Auparavant seul Concepts avait
  // le ♥ ; les 4 autres onglets manquaient le bouton, forcant l'apprenant à
  // aller dans [Base de connaissances] pour marquer un favori.
  const favKuIds = useFavoriteKuIds();
  // Eric (2026-05-18) — Modal d'import FAQ/Q&R/Scripts/Procédures depuis fichier
  // (CSV/JSON/MD/.docx/.xlsx) pré-câblé à la vidéo courante. Permet d'importer
  // une FAQ générée par un autre moyen (Google Sheet exporté en CSV/XLSX) et de
  // la voir apparaître dans les onglets de la vidéo comme une Q&R IA standard.
  const [importOpen, setImportOpen] = useState(false);
  const [inlineKuDraft, setInlineKuDraft] = useState({ title: "", question: "", answer: "" });
  // Vincent bug #81 (2026-05-07) — inline procedure editing in the video tab.
  const [editingProcedureId, setEditingProcedureId] = useState(null);
  const [procDraft, setProcDraft] = useState({ title: "", summary: "" });
  // Vincent (2026-05-12) — Génération auto de titre + historique pour les procédures
  // (parité avec Q&R). 3 propositions GPT-5.2 + historique des titres antérieurs.
  const [procTitleGenerating, setProcTitleGenerating] = useState(false);
  const [procTitleProposals, setProcTitleProposals] = useState([]);
  const [procTitleHistory, setProcTitleHistory] = useState([]);
  // Vincent 20/05/2026 — Modal de confirmation propre pour "Effacer l'historique" sur procédures
  // (remplace window.confirm() bloqué dans l'iframe Preview).
  const [procClearHistoryConfirm, setProcClearHistoryConfirm] = useState(null); // { procId, title }
  // Vincent (2026-07-22) — P1-4 Phase 3 refactor : hook usePipelineStatus.
  // Le hook gère le polling / cleanup / monotonic clamp. `loadVideoData`
  // sera défini plus bas — on utilise une ref pour éviter le décalage.
  const loadVideoDataRef = useRef(null);
  const loadAllTranscriptsRef = useRef(null);
  const { pipelineStatus, setPipelineStatus, startPolling } = usePipelineStatus({
    videoId,
    api: API,
    // Vincent B639 (Eric 2026-07-31 SPEC STRICK PROD) — Recharge AUSSI les
    // transcripts (Whisper + Fireflies + Fusionnée) après la fin de
    // transcription (paused_pending_*), pas seulement `data.segments`.
    onComplete: () => {
      if (loadVideoDataRef.current) loadVideoDataRef.current();
      if (loadAllTranscriptsRef.current) loadAllTranscriptsRef.current();
    },
  });
  // Eric 2026-07-20 (iter162 refactor phase 2) — Hook custom pour la
  // ré-analyse audio (fire-and-forget + polling). Extrait dans
  // /app/frontend/src/hooks/useSpeakerReprocess.js.
  // Bug 535 (Eric 2026-02-14) — Reçoit startPolling pour rafraîchir la
  // barre horizontale bleue du pipeline dès le clic du bouton.
  // Vincent B612 (Eric 2026-07-29 SPEC STRICK) — Après une ré-analyse audio
  // via /speakers/reprocess, on ne rechargeait QUE les speakers, pas la
  // vidéo. Résultat : les toggles Diarisation/ID faciale persistés dans
  // `video.use_diarisation` / `video.use_facial_id` ne se re-synchronisaient
  // pas côté UI, et la couleur des mots correspondants dans la barre
  // pipeline restait figée sur l'ancien état. Nouveau flux : on recharge
  // les speakers ET les métadonnées vidéo pour que la couleur du pipeline
  // (rouge/jaune/vert) reflète immédiatement les toggles réellement
  // appliqués.
  const loadVideoDataRefForReprocess = useRef(null);
  useEffect(() => { loadVideoDataRefForReprocess.current = loadVideoData; });
  const { speakerReprocessing, startReprocess: _startSpeakerReprocess } = useSpeakerReprocess({
    videoId,
    lang,
    reloadSpeakerData: async () => {
      await loadSpeakerData();
      // Recharge aussi la vidéo pour re-sync use_diarisation / use_facial_id
      // et donc la couleur des étapes du pipeline.
      if (loadVideoDataRefForReprocess.current) await loadVideoDataRefForReprocess.current();
    },
    startPolling,
  });
  // Bug 536 (Eric 2026-02-14) — Récupère les flags pipeline (Diarisation
  // On/Off, Identification faciale On/Off) pour colorer en rouge les
  // étapes correspondantes dans la barre horizontale bleue quand elles
  // sont désactivées globalement (org-level). Silencieux pour les
  // non-admins (endpoint admin uniquement — flags restent null → couleurs
  // normales).
  const [pipelineFlags, setPipelineFlags] = useState(null);
  useEffect(() => {
    if (!isAdmin) return;
    axios
      .get(`${API}/api/videos/admin/pipeline-flags`, { withCredentials: true })
      .then((r) => setPipelineFlags(r.data || null))
      .catch(() => setPipelineFlags(null));
  }, [isAdmin]);
  // Vincent (2026-02) bugs #524/#525 — Édition depuis Explorateur de fichiers :
  // remplacer les dropdowns codés en dur ("General/Onboarding/…" et "Niveau
  // 1/2/3/4") par la liste des catégories et parcours réels de l'organisation.
  const [categories, setCategories] = useState([]);
  const [allPathways, setAllPathways] = useState([]);

  // Vincent P1.8.d (Eric 2026-07-31 SPEC STRICK PROD) — Flux d'édition des
  // métadonnées vidéo (9 states + startEdit + saveEdit) migré dans le hook
  // custom `useVideoActions`. Le callback `onSaved` déclenche le rechargement
  // de la fiche vidéo + des catégories/parcours pour rafraîchir l'UI après
  // sauvegarde.
  const {
    editMode, setEditMode,
    editTitle, setEditTitle,
    editDesc, setEditDesc,
    editTheme, setEditTheme,
    editAccessLevel, setEditAccessLevel,
    editDropbox, setEditDropbox,
    editCategoryId, setEditCategoryId,
    editSelectedPathwayIds, setEditSelectedPathwayIds,
    editSaving,
    startEdit,
    saveEdit,
  } = useVideoActions({
    video: data?.video,
    videoId,
    api: API,
    allPathways,
    onSaved: () => { loadVideoData(); loadCategoriesAndPathways(); },
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(0);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  // Vincent #257 (2026-02-06) — Loading state pour l'extraction LLM des concepts.
  const [genConceptsLoading, setGenConceptsLoading] = useState(false);
  // Vincent XLSX 2026-07-13 21h Row 46213 — bouton "Générer/Effacer" pour Objections
  const [genObjectionsLoading, setGenObjectionsLoading] = useState(false);
  // Tâche 1283 — Nettoyage des objections : la modale propose les suppressions
  // (hors-sujet, orphelines, doublons), l'humain accepte ou refuse.
  const [objectionsCleanupOpen, setObjectionsCleanupOpen] = useState(false);
  // Vincent #256 (2026-02-06) — Loading state pour la régénération de titres Q&R.
  const [regenTitlesLoading, setRegenTitlesLoading] = useState(false);
  // Vincent #253 (2026-02-06) — Loading state pour le bulk-quizzable.
  const [bulkQuizLoading, setBulkQuizLoading] = useState(false);
  // Vincent #254/#255 (2026-02-06) — Loading state pour les imports .docx.
  const [importDocxLoading, setImportDocxLoading] = useState(false);
  // Vincent (Eric 2026-02-06) — Suggestions de parcours après pipeline complet.
  // Affiche un bandeau permettant à l'admin d'ajouter cette vidéo aux parcours
  // pertinents matchés sur training_theme / catégorie / tags.
  const [pathwaySuggestions, setPathwaySuggestions] = useState([]);
  const [pathwaySelected, setPathwaySelected] = useState(new Set());
  const [pathwayAdding, setPathwayAdding] = useState(false);
  const [pathwayBannerDismissed, setPathwayBannerDismissed] = useState(false);
  const importDocxFile = async (file, kuType) => {
    if (!file || importDocxLoading) return;
    if (!file.name.toLowerCase().endsWith(".docx")) {
      toast.error(lang === "fr" ? "Seul le format .docx est accepté" : "Only .docx is supported");
      return;
    }
    // Vincent #11 (Eric 2026-02-06) — Pour les procédures, on demande à l'admin
    // de choisir le mode d'interprétation : Liste (plusieurs procédures séparées
    // par des titres) ou Document (une seule procédure dont les paragraphes
    // sont les étapes). Auto par défaut.
    let mode = "auto";
    if (kuType === "procedure") {
      const choice = await promptDialog(
        tr(
          "Mode d'interprétation du document .docx :\n\n"
          + "• 1 = Liste (plusieurs procédures séparées par des titres Heading)\n"
          + "• 2 = Document (UNE SEULE procédure ; chaque paragraphe = une étape)\n"
          + "• 3 = Auto-détection (≥ 2 titres → Liste ; sinon → Document)\n\n"
          + "Tape 1, 2 ou 3 :",
          "How should the .docx document be read?\n\n"
          + "• 1 = List (several procedures separated by Heading titles)\n"
          + "• 2 = Document (ONE single procedure; each paragraph = one step)\n"
          + "• 3 = Auto-detect (2 or more titles → List; otherwise → Document)\n\n"
          + "Type 1, 2 or 3:",
        ),
        "3"
      );
      if (choice === null) return;  // user cancelled
      const c = (choice || "").trim();
      if (c === "1") mode = "list";
      else if (c === "2") mode = "document";
      else mode = "auto";
    }
    setImportDocxLoading(true);
    try {
      const fd = new FormData();
      fd.append("video_id", videoId);
      fd.append("file", file);
      fd.append("mode", mode);
      const { data } = await axios.post(
        `${API}/api/agent/knowledge-units/import-docx/${kuType}`,
        fd,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
      );
      const modeSuffix = kuType === "procedure" ? ` [mode: ${mode}]` : "";
      toast.success(lang === "fr"
        ? `${data.created} section${data.created > 1 ? "s" : ""} importée${data.created > 1 ? "s" : ""} depuis ${file.name}${modeSuffix}`
        : `${data.created} section(s) imported from ${file.name}${modeSuffix}`);
      loadVideoData();
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Erreur d'import .docx", ".docx import error"));
    } finally {
      setImportDocxLoading(false);
    }
  };
  // Transcript sub-tabs (Vincent spec): Whisper | Fireflies | Fusionnée
  // Vincent B681 (Eric 2026-02-XX) — transcriptSubTab + cross-validate state
  // migrés dans hooks/useTranscripts.js.
  // Vincent (2026-07-22) — P1-4 Phase 3 (suite) : hook useTranscriptGeneration.
  // allTranscripts + transcriptsLoading + loadAllTranscripts migrés dans
  // /app/frontend/src/hooks/useTranscriptGeneration.js — ~55 lignes économisées.
  // Vincent P1.9 (Eric 2026-07-31 SPEC STRICK PROD) — `onHydrateRejected`
  // mémoïsé pour que `loadAllTranscripts` reste une référence stable et
  // puisse être ajouté aux deps du useEffect de montage sans boucler.
  const onHydrateRejected = useCallback((hydrated) => setRejectedCorrections(hydrated), []);
  const { allTranscripts, setAllTranscripts, transcriptsLoading, loadAllTranscripts } = useTranscriptGeneration({
    videoId,
    api: API,
    onHydrateRejected,
  });
  // Vincent B681 (Eric 2026-02-XX) — useTranscripts pour cross-validate + polling
  const {
    transcriptSubTab,
    setTranscriptSubTab,
    crossValidateText,
    setCrossValidateText,
    crossValidating,
    setCrossValidating,
    crossValidateResult,
    setCrossValidateResult,
    pollMergedTranscript,
  } = useTranscripts({
    videoId,
    api: API,
    lang,
    setAllTranscripts,
    canEditKnowledge,
  });
  // Vincent #355 (Eric 2026-06-21 SPEC STRICK 3e passe) — override initial
  // subtab depuis l'URL (?source=whisper|fireflies|merged) si spécifié.
  useEffect(() => {
    if (urlSource === "merged" || urlSource === "fireflies" || urlSource === "whisper") {
      setTranscriptSubTab(urlSource);
    }
  }, [urlSource, setTranscriptSubTab]);
  // Vincent #355 — Force "merged" si non-éditeur (à chaque changement de droits)
  // Vincent #381 (Eric 2026-06-29 SPEC STRICK) — Si pas de fusion disponible,
  // on retombe sur "whisper" (transcription originale) pour le non-éditeur.
  // ⚠️ HOTFIX 2026-06-30 — `allTranscripts` doit être déclaré AVANT ce useEffect
  // (TDZ en build minifié : "Cannot access 'qa' before initialization").
  // Vincent Hook Deps Batch (Eric 2026-08-05 SPEC STRICK PROD, code review) —
  // Dépendance élargie de `allTranscripts?.summary?.has_merged` à
  // `allTranscripts` complet + `setTranscriptSubTab` : la règle
  // `react-hooks/exhaustive-deps` ne peut pas garantir la stabilité d'un
  // accès profond via optional chaining. Dep sur l'objet parent = plus safe,
  // et re-run reste économique (Array.diff référentielle).
  // Vincent B753 (Eric 2026-02-17) — Non-éditeur : force la source
  // canonique choisie par le concepteur (`video.canonical_transcript_source`),
  // fallback historique = merged → whisper.
  useEffect(() => {
    if (canEditKnowledge) return;
    const canon = data?.video?.canonical_transcript_source
      || (allTranscripts?.summary?.has_merged ? "merged" : "whisper");
    // Vérifie que la source canonique existe côté data ; sinon fallback.
    const hasIt = (canon === "merged" && allTranscripts?.summary?.has_merged)
      || (canon === "fireflies" && allTranscripts?.summary?.has_fireflies)
      || (canon === "whisper" && allTranscripts?.summary?.has_whisper);
    const target = hasIt
      ? canon
      : (allTranscripts?.summary?.has_merged ? "merged" : "whisper");
    if (transcriptSubTab !== target) {
      setTranscriptSubTab(target);
    }
  }, [canEditKnowledge, allTranscripts, transcriptSubTab, setTranscriptSubTab, data?.video?.canonical_transcript_source]);

  // Vincent B734 v2 (Eric 2026-02-14) — Toast « Fireflies déjà disponible ✓ »
  // dès que la page ouvre SI un Fireflies existe MAIS que Whisper n'est pas
  // encore terminé. Rassure l'utilisateur qu'il n'a pas à attendre le pipeline
  // pour consulter sa transcription Fireflies. Ne s'affiche qu'UNE seule fois
  // par ouverture de vidéo (guard via ref).
  const firefliesToastShownRef = useRef(false);
  // Reset la garde quand on change de vidéo (SPA navigation) pour que le
  // toast puisse ré-apparaître si l'utilisateur ouvre une autre vidéo.
  useEffect(() => {
    firefliesToastShownRef.current = false;
  }, [videoId]);
  useEffect(() => {
    if (!allTranscripts?.summary) return;
    const { has_fireflies, has_whisper } = allTranscripts.summary;
    // Condition : Fireflies dispo ET Whisper pas encore prêt → cas où l'info
    // « pas besoin d'attendre » est réellement utile. Si les deux sont là,
    // pas de toast (info inutile). Si aucun, pas de toast non plus.
    if (has_fireflies && !has_whisper && !firefliesToastShownRef.current) {
      firefliesToastShownRef.current = true;
      toast.success(
        lang === "fr"
          ? "Fireflies déjà disponible ✓ — Consultable dès maintenant dans l'onglet Transcriptions › Fireflies, sans attendre Whisper."
          : "Fireflies already available ✓ — Readable now in the Transcriptions › Fireflies tab, no need to wait for Whisper.",
        {
          duration: 6000,
          id: `fireflies-ready-${videoId}`,   // dédup par vidéo (SPA navigation)
          data: { testid: "fireflies-early-available-toast" },
        },
      );
    }
    // Reset le flag quand la vidéo change (SPA route change réutilise le composant)
  }, [allTranscripts?.summary?.has_fireflies, allTranscripts?.summary?.has_whisper, videoId, lang]);
  // Transcript modification history (Undo / audit trail)
  const [showHistory, setShowHistory] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [revisionsLoading, setRevisionsLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);
  // Eric 2026-05-12 — Modale d'aide raccourcis clavier (touche `?`)
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);

  const videoRef = useRef(null);
  // Vincent #269 (2026-06-11) — Garde anti-doublon pour le heartbeat 30s
  const watchProgressSentRef = useRef(false);
  // Vincent #394 (Eric 2026-07-01 SPEC STRICK — À REFAIRE) — Heartbeat périodique
  // pour propager la durée max ; envoie tous les +15 s après le 1er.
  const lastWatchSentRef = useRef(0);
  // Vincent (2026-07-22) — P1-4 Phase 3 : pollingRef migré dans le hook
  // usePipelineStatus. Auto-cleanup géré par le hook au unmount.
  const transcriptContainerRef = useRef(null);
  const activeSegmentRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const progressRef = useRef(null);
  const playerContainerRef = useRef(null);

  const loadCategoriesAndPathways = useCallback(async () => {
    try {
      const [catsRes, pathsRes] = await Promise.all([
        cachedGet(axios, `${API}/api/content-categories`, { ttl: 300000 }),
        cachedGet(axios, `${API}/api/pathways`, { ttl: 300000 }),
      ]);
      setCategories(Array.isArray(catsRes.data) ? catsRes.data : []);
      setAllPathways(Array.isArray(pathsRes.data) ? pathsRes.data : (pathsRes.data?.pathways || []));
    } catch (e) {
      console.warn("[VideoDetailPage] loadCategoriesAndPathways failed", e);
    }
  }, []);

  // Vincent 2026-05-06 (bug #64) — fetch existing notebook procedure doc if any
  const loadProcedureDoc = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/videos/${videoId}/procedure-doc`, { withCredentials: true });
      setNotebookProcedureDoc(data);
    } catch {
      // 404 = pas encore généré, c'est OK
      setNotebookProcedureDoc(null);
    }
  }, [videoId]);

  // ── Data loading ──
  useEffect(() => {
    // Vincent B797 (Eric 2026-02-12) — Reset des états locaux non-gérés
    // par useVideoData pour éviter d'afficher les données de la vidéo
    // précédente (durée, contenu vidéo, procedureDoc, speakerData…)
    // pendant la transition Précédent/Suivant. useVideoData réinitialise
    // déjà `data` et `loading` de son côté. Les états player-locaux
    // (currentTime, isPlaying, duration…) sont remis à zéro pour ne pas
    // pointer sur des timestamps de l'ancienne vidéo.
    setNotebookProcedureDoc(null);
    setSpeakerData(null);
    setSpeakerMapping({});
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
    setIsBuffering(true);
    setVideoError(null);
    setRejectedCorrections([]);
    setEditingCorrection(null);
    loadVideoData();
    loadProcedureDoc();
    // Vincent #393 (Eric 2026-06-29 SPEC STRICK) — Précharger les 3
    // transcripts (originale/Fireflies/fusionnée) au montage de la vidéo
    // pour que le menu déroulant Transcriptions reflète immédiatement leur
    // existence (sinon on laisse croire qu'il n'y a que l'originale).
    loadAllTranscripts();
    // Vincent (2026-02) #524/#525 — Précharger les catégories + parcours
    // pour que les dropdowns d'édition affichent la vraie liste de l'org
    // au lieu des valeurs codées en dur ("General/Onboarding/Niveau 1…").
    loadCategoriesAndPathways();
    // Vincent B696 (Eric 2026-02-08) — Précharger speakerData au montage
    // pour que l'édition per-segment dans l'onglet Whisper (SpeakerCell)
    // ait les options du menu déroulant disponibles IMMÉDIATEMENT, sans
    // exiger de Vincent qu'il ouvre d'abord l'onglet Locuteurs. Le
    // fallback speakerOptions inclut speakerData.known_speakers.
    loadSpeakerData();
    // Le cleanup du polling est maintenant dans usePipelineStatus.
  }, [videoId, loadVideoData, loadProcedureDoc, loadAllTranscripts, loadCategoriesAndPathways]);

  // Vincent B807 v5 (Eric 2026-02-13 — 7e récurrence, VRAIE cause) —
  // COURSE ENTRE UPLOAD ET AFFICHAGE. Le sync ingestion Fireflies dans
  // /direct/complete (ou /upload) prend 2-5s (HEAD S3 + GET S3 + extract
  // + parse + insert secondary_transcripts). Vincent clique « Voir la
  // vidéo » IMMÉDIATEMENT → `loadAllTranscripts()` fetche AVANT que
  // l'insert soit fini → `summary.has_fireflies=false` → dropdown reste
  // « non importée ». Pas de refetch avant `usePipelineStatus.onComplete`
  // (à la fin de Whisper, 5-10 min plus tard).
  //
  // Fix : polling léger `/transcripts/all` toutes les 3 s tant que le
  // video a `fireflies_storage_path` OU `fireflies_extraction_failed`
  // (signale qu'un fichier a été fourni) mais `summary.has_fireflies`
  // est false. Max 20 tentatives (60 s) — suffisant pour un ingestion
  // sync raisonnable. Puis on abandonne.
  useEffect(() => {
    if (!data?.video || !videoId) return;
    const ffProvided = !!(data.video.fireflies_storage_path || data.video.fireflies_extraction_failed);
    const ffLoaded = !!allTranscripts?.summary?.has_fireflies;
    if (!ffProvided || ffLoaded) return;  // rien à surveiller
    let attempts = 0;
    const maxAttempts = 20;
    // Vincent B1017 (Eric 2026-02-26 SPEC STRICK PROD) — AbortController
    // pour couper la requête en vol si Vincent quitte la page vidéo
    // pendant que le poll est en attente d'une réponse.
    const controller = new AbortController();
    const tid = setInterval(async () => {
      attempts += 1;
      if (attempts > maxAttempts) {
        clearInterval(tid);
        return;
      }
      try {
        // Vincent B956 (Eric 2026-02-14 SPEC STRICK PROD) — Poll léger :
        // on ne veut que le bloc summary (has_fireflies) pour flipper
        // l'état. summary_only=true économise ~100kB-2MB par appel
        // (transcripts complets = 5000+ segments).
        const { data: summary } = await axios.get(
          `${API}/api/videos/${videoId}/transcripts/all?summary_only=true`,
          {
            withCredentials: true,
            // Vincent B1015 (Eric 2026-02-26) — Poll toutes les 3s :
            // pas de retry (le prochain poll fait office de retry).
            _maxRetries: 1,
            silent: true,
            signal: controller.signal,  // Vincent B1017 — abort on unmount
          },
        );
        if (summary?.summary?.has_fireflies) {
          // Fireflies vient d'apparaître — fetch complet pour peupler
          // les segments dans l'UI.
          await loadAllTranscripts();
          clearInterval(tid);
        }
      } catch {
        // silencieux — le polling continue (ou est abort)
      }
    }, 3000);
    return () => {
      clearInterval(tid);
      controller.abort();  // Vincent B1017 — coupe requête en vol au unmount
    };
  }, [videoId, data?.video?.fireflies_storage_path, data?.video?.fireflies_extraction_failed, allTranscripts?.summary?.has_fireflies]);


  // Vincent #139 (Eric 2026-05-13) — Helpers pour génération + historique de titres Q&R inline.
  const fetchQaTitleHistory = async (kuId) => {
    try {
      const { data } = await axios.get(`${API}/api/agent/knowledge-units/${kuId}/history`, { withCredentials: true });
      const editHistory = data?.edit_history || [];
      const titleEntries = editHistory
        .filter(e => (e.fields_changed || []).includes("title") && (e.previous_title || "").trim())
        .map(e => ({ title: e.previous_title, replaced_at: e.edited_at, replaced_by_email: e.edited_by_email }));
      return titleEntries.slice(-20);
    } catch { return []; }
  };

  const handleQaGenerateTitle = async (ku) => {
    if (qaTitleAi.generating) return;
    setQaTitleAi(s => ({ ...s, kuId: ku.id, generating: true, proposals: [] }));
    try {
      const { data } = await axios.post(
        `${API}/api/agent/knowledge-units/${ku.id}/generate-title`,
        {}, { withCredentials: true },
      );
      const proposals = Array.isArray(data?.proposals) ? data.proposals : (data?.title ? [data.title] : []);
      const history = await fetchQaTitleHistory(ku.id);
      setQaTitleAi({ kuId: ku.id, generating: false, proposals, history });
      if (proposals.length === 0) {
        // Vincent #151/#154 — Message explicite du backend (ex: timeout, quota,
        // clé invalide) au lieu du générique "Aucune proposition reçue".
        const explicit = data?.error || (lang === "fr"
          ? "Aucune proposition reçue de l'IA. Causes possibles : clé OpenAI manquante, quota dépassé, ou format de réponse invalide. Vérifiez Forfait AI et réessayez."
          : "No proposals received. Check Forfait AI and retry.");
        toast.error(explicit, { duration: 8000 });
      } else {
        // Auto-pick the first proposal as the draft title.
        setInlineKuDraft(d => ({ ...d, title: proposals[0] }));
        toast.success(lang === "fr" ? `${proposals.length} proposition(s) générée(s)` : `${proposals.length} proposal(s) generated`);
      }
    } catch (e) {
      setQaTitleAi(s => ({ ...s, generating: false }));
      // Vincent #151 — Détailler les codes HTTP et erreurs réseau plutôt que "Erreur de génération"
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      let msg;
      if (status === 404) {
        msg = lang === "fr"
          ? "Élément introuvable (Q&R, script, procédure ou concept) — il a peut-être été supprimé, rechargez la page."
          : "Q&A not found.";
      } else if (status === 403) {
        msg = lang === "fr"
          ? "Vous n'avez pas les droits pour générer un titre. Seul un admin peut le faire."
          : "Only admins can generate titles.";
      } else if (status === 402 || (detail && detail.includes && detail.includes("clé"))) {
        msg = lang === "fr"
          ? "Clé OpenAI non configurée. Allez dans Forfait AI > Connecter une clé."
          : "OpenAI key not configured.";
      } else if (!e.response) {
        msg = lang === "fr"
          ? "Connexion au serveur perdue. Vérifiez votre Internet et réessayez."
          : "Network error.";
      } else {
        msg = detail || (lang === "fr" ? `Erreur ${status || ""} : ${e.message}` : `Error: ${e.message}`);
      }
      toast.error(msg, { duration: 8000 });
    }
  };

  const handleQaClearHistoryConfirmed = async () => {
    const ku = qaClearHistoryConfirm;
    if (!ku) return;
    try {
      await axios.delete(`${API}/api/agent/knowledge-units/${ku.id}/title-history`, { withCredentials: true });
      setQaTitleAi(s => ({ ...s, kuId: ku.id, history: [] }));
      toast.success(lang === "fr" ? "Historique des titres effacé" : "Title history cleared");
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Échec", "Failed"));
    } finally {
      setQaClearHistoryConfirm(null);
    }
  };

  const openInlineQaEdit = async (ku) => {
    setEditingInlineKuId(ku.id);
    setInlineKuDraft({ title: ku.title || "", question: ku.content?.question || "", answer: ku.content?.answer || "" });
    const history = await fetchQaTitleHistory(ku.id);
    setQaTitleAi({ kuId: ku.id, generating: false, proposals: [], history });
  };

  // Vincent bug #27/#107 (2026-05-08) — Modal Rapport en markdown (Q&R / Scripts / Procédures).
  // Construit le markdown côté client à partir des knowledge_units locaux pour éviter une roundtrip backend.
  // Bouton "Imprimer/PDF" ouvre une nouvelle fenêtre avec window.print() pour exporter en PDF natif.
  const openReportModal = (type) => {
    const videoTitle = data?.video?.title || data?.video?.filename || "Vidéo";
    const dateStr = new Date().toLocaleDateString("fr-CA");
    let markdown = "";
    let title = "";
    if (type === "qa") {
      const items = (knowledge_units || []).filter(k => k.type === "qa");
      title = `Rapport Q&R — ${videoTitle}`;
      markdown = `# ${title}\n\n_Généré le ${dateStr} · ${items.length} Q&R_\n\n`;
      const approved = items.filter(k => k.status === "approved");
      const pending = items.filter(k => k.status !== "approved" && k.status !== "rejected");
      if (approved.length) {
        markdown += `## Q&R approuvées (${approved.length})\n\n`;
        approved.forEach((k, i) => {
          markdown += `### ${i + 1}. ${k.title || k.content?.question || "Sans titre"}\n\n`;
          if (k.content?.question) markdown += `**Question** : ${k.content.question}\n\n`;
          if (k.content?.answer) markdown += `**Réponse** : ${k.content.answer}\n\n`;
        });
      }
      if (pending.length) {
        markdown += `## Q&R en révision (${pending.length})\n\n`;
        pending.forEach((k, i) => {
          markdown += `### ${i + 1}. ${k.title || k.content?.question || "Sans titre"}\n\n`;
          if (k.content?.question) markdown += `**Question** : ${k.content.question}\n\n`;
          if (k.content?.answer) markdown += `**Réponse** : ${k.content.answer}\n\n`;
        });
      }
      if (items.length === 0) markdown += "_Aucune Q&R générée pour cette vidéo._\n";
    } else if (type === "scripts") {
      const items = (knowledge_units || []).filter(k => k.type === "script");
      title = `Rapport Scripts de vente — ${videoTitle}`;
      markdown = `# ${title}\n\n_Généré le ${dateStr} · ${items.length} script(s)_\n\n`;
      items.forEach((k, i) => {
        markdown += `## ${i + 1}. ${k.title || "Script sans titre"}\n\n`;
        const c = k.content || {};
        if (c.theme) markdown += `**Thème** : ${c.theme}\n\n`;
        if (Array.isArray(c.v1) && c.v1.length) {
          markdown += `### V1 — Verbatim\n\n`;
          c.v1.forEach(t => { markdown += `- **${t.role || t.speaker || "?"}** : ${t.text || ""}\n`; });
          markdown += "\n";
        }
        if (Array.isArray(c.v2) && c.v2.length) {
          markdown += `### V2 — Reformulé\n\n`;
          c.v2.forEach(t => { markdown += `- ${t.text || ""}\n`; });
          markdown += "\n";
        }
        if (Array.isArray(c.v3) && c.v3.length) {
          markdown += `### V3 — Justifié\n\n`;
          c.v3.forEach(t => { markdown += `- ${t.text || ""}${t.justification ? ` _(${t.justification})_` : ""}\n`; });
          markdown += "\n";
        }
      });
      if (items.length === 0) markdown += "_Aucun script généré pour cette vidéo._\n";
    } else if (type === "procedures") {
      title = `Rapport Procédures — ${videoTitle}`;
      markdown = `# ${title}\n\n_Généré le ${dateStr}_\n\n`;
      if (notebookProcedureDoc?.markdown_content) {
        markdown += notebookProcedureDoc.markdown_content;
      } else {
        const items = (knowledge_units || []).filter(k => k.type === "procedure" || k.type === "process");
        items.forEach((k, i) => {
          markdown += `## ${i + 1}. ${k.title || "Procédure"}\n\n`;
          if (k.content?.summary) markdown += `${k.content.summary}\n\n`;
        });
        if (items.length === 0) markdown += "_Aucune procédure générée pour cette vidéo._\n";
      }
    } else if (type === "fusion") {
      // Eric / Vincent (2026-05-13) — Rapport de fusion de transcription extrait
      // dans une modale dédiée pour ne plus polluer le bas du transcript.
      title = `Rapport de fusion — ${videoTitle}`;
      const report = allTranscripts?.merged?.analysis_report || "";
      const diag = allTranscripts?.merged?.fusion_diagnostic || {};
      const corrCount = allTranscripts?.merged?.corrections_count || 0;
      const segCount = allTranscripts?.merged?.modified_segments_count || 0;
      markdown = `# ${title}\n\n_Généré le ${dateStr}_\n\n`;
      markdown += `- **Corrections appliquées** : ${corrCount}\n`;
      markdown += `- **Segments modifiés** : ${segCount}\n`;
      if (typeof diag.minor_corrections_count === "number") {
        markdown += `- **Mineures (orthographe / accents / ponctuation)** : ${diag.minor_corrections_count}\n`;
      }
      if (typeof diag.major_corrections_count === "number") {
        markdown += `- **Majeures (remplacements de mots)** : ${diag.major_corrections_count}\n`;
      }
      if (diag.meeting_kind) {
        markdown += `- **Type de rencontre détecté** : ${diag.meeting_kind.replace(/_/g, " ")}\n`;
      }
      if (Array.isArray(diag.distinct_final_speakers) && diag.distinct_final_speakers.length) {
        markdown += `- **Locuteurs finaux** : ${diag.distinct_final_speakers.join(", ")}\n`;
      }
      markdown += `\n`;
      if (report) {
        markdown += report;
      } else {
        markdown += `_Aucun rapport d'analyse GPT-5.2 disponible pour cette fusion (timeout ou erreur de génération)._\n`;
      }
    }
    setReportModal({ type, title, markdown });
  };

  // Eric / Vincent (2026-05-14, Refacto Option A Phase 3) — formatTimecode,
  // downloadReportPDF, downloadReportMarkdown extraits dans /lib/videoReportHelpers.js
  // pour reduire VideoDetailPage et permettre les tests unitaires.

  // Vincent (2026-07-22) — P1-4 Phase 3 : le hook usePipelineStatus a besoin
  // d'appeler loadVideoData quand le pipeline devient "complete". Depuis
  // P1.8.c, `loadVideoData` est stable (useCallback dans useVideoData) mais
  // on garde la ref-indirection pour rester cohérent avec les autres
  // consommateurs (useSpeakerReprocess, handlers legacy).
  useEffect(() => { loadVideoDataRef.current = loadVideoData; });
  // Vincent B639 (Eric 2026-07-31 SPEC STRICK PROD) — Idem pour loadAllTranscripts
  // pour rafraîchir aussi l'onglet Fusionnée quand le pipeline entre en pause.
  useEffect(() => { loadAllTranscriptsRef.current = loadAllTranscripts; });

  // Vincent P1.8.c (Eric 2026-07-31 SPEC STRICK PROD) — Side-effects post-fetch
  // extraits de l'ancien `loadVideoData` inline. Chaque effet est isolé, réactif
  // à `data?.video?.pipeline_status`, et gardé par un ref one-shot quand
  // nécessaire pour ne pas re-déclencher à chaque refetch.

  // (1) Auto-démarrage du polling quand le pipeline est en cours.
  useEffect(() => {
    const status = data?.video?.pipeline_status;
    if (status === "processing" || status === "pending") {
      startPolling();
    }
  }, [data?.video?.pipeline_status, startPolling]);

  // (2) Track-view + suggestions de parcours — une seule fois à la complétion.
  const trackedViewRef = useRef(false);
  useEffect(() => {
    if (data?.video?.pipeline_status !== "complete") return;
    if (trackedViewRef.current) return;
    trackedViewRef.current = true;
    axios.post(`${API}/api/videos/${videoId}/track-view`, {}, { withCredentials: true }).catch(() => {});
    // Vincent (Eric 2026-02-06) — Charge les suggestions de parcours
    // une seule fois (à la complétion du pipeline). Best-effort, silencieux
    // en cas d'erreur (auth ou tenant mismatch).
    if (isAdmin && pathwaySuggestions.length === 0 && !pathwayBannerDismissed) {
      axios.get(`${API}/api/pathways/suggest-for-video/${videoId}`, { withCredentials: true })
        .then(r => setPathwaySuggestions(r.data?.suggestions || []))
        .catch(() => {});
    }
  }, [data?.video?.pipeline_status, videoId, isAdmin, pathwaySuggestions.length, pathwayBannerDismissed]);

  // (3) Auto-seek `?t=<sec>` — une seule fois au 1er chargement de `data`.
  const autoSeekedRef = useRef(false);
  useEffect(() => {
    if (!data) return;
    if (autoSeekedRef.current) return;
    if (!(startTime > 0)) { autoSeekedRef.current = true; return; }
    autoSeekedRef.current = true;
    setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.currentTime = startTime;
        videoRef.current.play().catch(() => {});
      }
    }, 1000);
  }, [data, startTime]);


  const loadSpeakerData = async () => {
    try {
      const { data: sd } = await axios.get(`${API}/api/videos/${videoId}/speakers`, { withCredentials: true });
      setSpeakerData(sd);
      if (sd.speaker_mapping && Object.keys(sd.speaker_mapping).length > 0) {
        setSpeakerMapping(sd.speaker_mapping);
      } else {
        const autoMap = {};
        (sd.detected_speakers || []).forEach(s => { autoMap[s] = s; });
        setSpeakerMapping(autoMap);
      }
    } catch (e) { console.error("Speaker data load error:", e); }
  };

  const handleValidateSpeakers = async () => {
    // Vincent B917 (Eric 2026-02-14 SPEC STRICK PROD) — Régression PROD :
    // "on a perdu la fonction Valider les locuteurs, qui ne fait plus rien".
    //
    // Root cause : le handler swallowait toute erreur silencieusement
    // (catch → console.error) et n'affichait JAMAIS de toast, même en
    // succès. Résultat côté utilisateur : on clique, la spinner tourne
    // brièvement, puis plus rien de visible — même si le backend a
    // renvoyé 200 avec 0 segments modifiés (cas fréquent quand le
    // `speakerMapping` ne matche aucun `original_speaker` du segment).
    //
    // Fix :
    //   • Guard client-side : si mapping est vide ou identique aux
    //     labels génériques (aucun renommage réel), on refuse.
    //   • Toast succès avec le nombre de segments mis à jour.
    //   • Toast erreur avec détail HTTP quand ça casse.
    if (!speakerMapping || Object.keys(speakerMapping).length === 0) {
      toast.error(lang === "fr"
        ? "Aucun locuteur à valider — le mapping est vide."
        : "No speaker to validate — mapping is empty.");
      return;
    }
    setSpeakerSaving(true);
    try {
      const uniqueTargets = [...new Set(Object.values(speakerMapping))];
      let resp;
      if (uniqueTargets.length === 1 && speakerData?.detected_speakers?.length > 1) {
        resp = await axios.post(`${API}/api/videos/${videoId}/speakers/assign-single`, { speaker_name: uniqueTargets[0] }, { withCredentials: true });
      } else {
        resp = await axios.post(`${API}/api/videos/${videoId}/speakers/validate`, { mapping: speakerMapping }, { withCredentials: true });
      }
      const nSeg = resp?.data?.segments_updated ?? 0;
      if (nSeg > 0) {
        toast.success(lang === "fr"
          ? `Identification validée (${nSeg} segments mis à jour).`
          : `Identification validated (${nSeg} segments updated).`);
      } else {
        // Cas typique : `speakerMapping` = {"Speaker A": "Speaker A"} — le
        // backend renvoie 200 mais 0 segment modifié. On informe pour éviter
        // que Vincent croie que "rien ne se passe".
        toast.info(lang === "fr"
          ? "Validation enregistrée — aucun segment renommé (les noms étaient déjà à jour)."
          : "Validation saved — no segment renamed (names were already up-to-date).");
      }
      await loadSpeakerData();
      loadVideoData();
    } catch (e) {
      console.error(e);
      const detail = e?.response?.data?.detail || e?.message || (lang === "fr" ? "erreur inconnue" : "unknown error");
      toast.error((lang === "fr" ? "Échec de la validation : " : "Validation failed: ") + detail);
    }
    setSpeakerSaving(false);
  };

  // Vincent (2026-07-22) — loadAllTranscripts migré dans le hook
  // useTranscriptGeneration (voir déclaration en tête de composant).

  const loadRevisions = async () => {
    setRevisionsLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/videos/${videoId}/transcript-revisions`, { withCredentials: true });
      setRevisions(data?.revisions || []);
    } catch (e) {
      console.error("loadRevisions error:", e);
      toast.error(lang === "fr" ? "Erreur lors du chargement de l'historique" : "Failed to load history");
    } finally {
      setRevisionsLoading(false);
    }
  };

  const handleRestoreRevision = async (revId) => {
    const ok = await confirmDialog(lang === "fr"
      ? "Restaurer cette version ? L'etat actuel sera d'abord sauvegarde (vous pourrez l'annuler a nouveau)."
      : "Restore this version? Current state will be saved first (you can undo again).");
    if (!ok) return;
    setRestoring(revId);
    try {
      await axios.post(`${API}/api/videos/${videoId}/transcript-revisions/${revId}/restore`, {}, { withCredentials: true });
      toast.success(lang === "fr" ? "Version restauree" : "Version restored");
      await loadAllTranscripts();
      await loadRevisions();
    } catch (e) {
      toast.error(lang === "fr"
        ? `Erreur : ${e.response?.data?.detail || e.message}`
        : `Error: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRestoring(null);
    }
  };

  // Vincent XLSX 2026-07-15 Rows 478 + 487 (Eric 2026-07-16 SPEC STRICK 2e passe) —
  // État pour la sélection multiple des locuteurs présents dans la vidéo +
  // toggles Diarisation ON/OFF et Identification faciale ON/OFF (bypass).
  const [presentSpeakers, setPresentSpeakers] = useState(new Set());
  const [useDiarisation, setUseDiarisation] = useState(true);
  const [useFacialId, setUseFacialId] = useState(true);

  // Vincent B550 (Eric 2026-07-24) — Synchronise les toggles des cases
  // "Diarisation" et "Identification faciale" avec les valeurs persistées
  // sur la vidéo. Sans ça, elles restaient toujours à ON par défaut et
  // divergaient de l'état réellement appliqué lors du dernier traitement.
  //
  // Vincent B549 REDO (Eric 2026-07-25) — Le B550 ne suffit pas : quand la
  // vidéo n'a PAS de champ `use_diarisation` persisté mais que le kill switch
  // global `skip_assemblyai_diarization` est actif, le pipeline s'exécute en
  // mode bypass (texte ROUGE) mais la case reste cochée ON par défaut,
  // incohérence flagrante remontée dans le JSON payload de Vincent 2026-07-24.
  // Fix : les cases DOIVENT refléter exactement la même prédicat que la
  // couleur du texte dans VideoPipelineStatus (rouge = OFF, vert/bleu = ON) :
  //   - si `use_diarisation` est booléen sur la vidéo → utilise cette valeur
  //   - sinon si kill switch global actif → false (case OFF)
  //   - sinon → true (case ON par défaut)
  //
  // Vincent P1.9 (Eric 2026-07-31 SPEC STRICK PROD) — Deps : on liste les 3
  // champs scalaires lus au lieu de `data?.video` (référence complète) pour
  // éviter que l'effet ne re-tire à chaque `setData` alors que les champs
  // pertinents n'ont pas changé. Le champ `.id` couvre déjà le cas
  // vidéo → null.
  useEffect(() => {
    const v = data?.video;
    if (!v) return;
    // Diarisation — priorité : per-video > kill switch global > défaut ON
    if (typeof v.use_diarisation === "boolean") {
      setUseDiarisation(v.use_diarisation);
    } else if (pipelineFlags?.skip_assemblyai_diarization === true) {
      setUseDiarisation(false);
    } else {
      setUseDiarisation(true);
    }
    // Identification faciale — même logique
    if (typeof v.use_facial_id === "boolean") {
      setUseFacialId(v.use_facial_id);
    } else if (pipelineFlags?.skip_visual_speaker_id === true) {
      setUseFacialId(false);
    } else {
      setUseFacialId(true);
    }
    // Vincent Hook Deps Batch (Eric 2026-08-05, code review) — Dep sur
    // `data?.video` complet plutôt que sur 3 accès profonds : la règle
    // exhaustive-deps ne peut pas prouver la stabilité de sous-champs.
    // Re-run économique (comparaison référentielle sur l'objet parent).
  }, [
    data?.video,
    pipelineFlags?.skip_assemblyai_diarization,
    pipelineFlags?.skip_visual_speaker_id,
  ]);

  const handleReprocessSpeakers = async () => {
    // Eric 2026-07-20 (iter162 refactor phase 2) — Logic extraite dans le
    // hook `useSpeakerReprocess` (polling fire-and-forget + toasts +
    // fallback legacy sync). VideoDetailPage.js n'a plus que le mapping
    // props → hook args.
    await _startSpeakerReprocess({
      selectedSpeakers: Array.from(presentSpeakers),
      useDiarisation,
      useFacialId,
    });
  };

  // Vincent P1.8.d (Eric 2026-07-31 SPEC STRICK PROD) — `startEdit` + `saveEdit`
  // migrés dans `hooks/useVideoActions.js`.

  // Vincent (2026-07-22) — P1-4 Phase 3 refactor : startPolling +
  // fetchPipelineStatus + monotonic clamp migrés dans le hook custom
  // usePipelineStatus (frontend/src/hooks/usePipelineStatus.js) —
  // ~30 lignes économisées ici + testable isolément.

  // ── Video controls ──
  const togglePlay = useCallback(() => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) videoRef.current.play().catch(() => {});
    else videoRef.current.pause();
  }, []);

  const seekTo = useCallback((time) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      videoRef.current.play().catch(() => {});
    }
  }, []);

  const skip = useCallback((delta) => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(0, Math.min(duration, videoRef.current.currentTime + delta));
    }
  }, [duration]);

  const toggleMute = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  }, [isMuted]);

  const changeVolume = useCallback((v) => {
    if (videoRef.current) {
      videoRef.current.volume = v;
      setVolume(v);
      if (v === 0) { setIsMuted(true); videoRef.current.muted = true; }
      else if (isMuted) { setIsMuted(false); videoRef.current.muted = false; }
    }
  }, [isMuted]);

  const changeSpeed = useCallback((speed) => {
    if (videoRef.current) {
      videoRef.current.playbackRate = speed;
      setPlaybackSpeed(speed);
      setShowSpeedMenu(false);
    }
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!playerContainerRef.current) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else playerContainerRef.current.requestFullscreen();
  }, []);

  const togglePiP = useCallback(async () => {
    if (!videoRef.current) return;
    try {
      if (document.pictureInPictureElement) await document.exitPictureInPicture();
      else await videoRef.current.requestPictureInPicture();
    } catch (e) {
      // Vincent P1 (Eric 2026-08-05) — PiP peut échouer si le navigateur
      // n'expose pas la Picture-in-Picture API (Safari iOS/Firefox) ou si
      // l'utilisateur refuse. On loggue pour tracer, pas de toast bruyant.
      console.warn("[PiP] toggle failed:", e);
    }
  }, []);

  // ── Keyboard shortcuts ──
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable) return;
      // Ignorer si Cmd/Ctrl/Alt → laisse passer les raccourcis OS / navigateur
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      switch (e.key) {
        // Lecture
        case " ":
        case "k":
        case "K":
          e.preventDefault(); togglePlay(); break;
        // Navigation temporelle
        case "ArrowLeft": e.preventDefault(); skip(-10); break;
        case "ArrowRight": e.preventDefault(); skip(10); break;
        case "j": case "J": e.preventDefault(); skip(-5); break;
        case "l": case "L": e.preventDefault(); skip(5); break;
        case "0":
          e.preventDefault();
          if (videoRef.current) { videoRef.current.currentTime = 0; }
          break;
        // Volume
        case "ArrowUp": e.preventDefault(); changeVolume(Math.min(1, volume + 0.1)); break;
        case "ArrowDown": e.preventDefault(); changeVolume(Math.max(0, volume - 0.1)); break;
        case "m": case "M": e.preventDefault(); toggleMute(); break;
        // Vue
        case "f": case "F": e.preventDefault(); toggleFullscreen(); break;
        case "p": case "P": e.preventDefault(); togglePiP(); break;
        // Splitter (raccourcis Knoria)
        case "v": case "V":
          e.preventDefault();
          // Toggle entre mode empilé (100) ↔ côte-à-côte (60)
          toggleStacked();
          break;
        case "c": case "C":
          e.preventDefault();
          resetSplit();
          break;
        // Vitesse de lecture (1=0.5x, 2=0.75x, 3=1x, 4=1.25x, 5=1.5x, 6=1.75x, 7=2x)
        case "1": e.preventDefault(); changeSpeed(0.5); break;
        case "2": e.preventDefault(); changeSpeed(0.75); break;
        case "3": e.preventDefault(); changeSpeed(1); break;
        case "4": e.preventDefault(); changeSpeed(1.25); break;
        case "5": e.preventDefault(); changeSpeed(1.5); break;
        case "6": e.preventDefault(); changeSpeed(1.75); break;
        case "7": e.preventDefault(); changeSpeed(2); break;
        // Aide
        case "?":
        case "/":
          if (e.key === "?" || e.shiftKey) {
            e.preventDefault();
            setShowShortcutsHelp((v) => !v);
          }
          break;
        case "Escape":
          if (showShortcutsHelp) { e.preventDefault(); setShowShortcutsHelp(false); }
          break;
        default: break;
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [togglePlay, skip, changeVolume, volume, toggleMute, toggleFullscreen, togglePiP, changeSpeed, showShortcutsHelp, toggleStacked, resetSplit]);

  // ── Controls auto-hide ──
  const showControls = useCallback(() => {
    setControlsVisible(true);
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) setControlsVisible(false);
    }, 3000);
  }, [isPlaying]);

  // ── Video events ──
  const handleTimeUpdate = () => {
    if (videoRef.current && !isSeeking) {
      setCurrentTime(videoRef.current.currentTime);
      if (videoRef.current.duration && !isNaN(videoRef.current.duration)) {
        setDuration(videoRef.current.duration);
      }
      // Vincent #269 (2026-06-11) → #394 (Eric 2026-07-01 SPEC STRICK) —
      // Heartbeat périodique : 1er envoi à 30 s, puis toutes les +15 s pour
      // propager la durée max réellement visionnée (backend fait max()).
      const t = videoRef.current.currentTime;
      const shouldSend =
        (!watchProgressSentRef.current && t >= 30) ||
        (watchProgressSentRef.current && t - lastWatchSentRef.current >= 15);
      if (shouldSend) {
        const wasFirst = !watchProgressSentRef.current;
        watchProgressSentRef.current = true;
        lastWatchSentRef.current = t;
        axios.post(
          `${API}/api/videos/${videoId}/watch-progress`,
          { seconds: t },
          { withCredentials: true }
        ).catch(() => { if (wasFirst) watchProgressSentRef.current = false; });
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      const d = videoRef.current.duration;
      if (d && !isNaN(d)) setDuration(d);
      else if (data?.transcript?.duration) setDuration(data.transcript.duration);
      setIsBuffering(false);
    }
  };

  const handleVideoError = (e) => {
    // Bogue 1199 (Éric 2026-06-25) — « Tu es maintenant incapable de lire les
    // vidéos que tu savais encore lire hier. »
    //
    // Vérifié côté serveur : le flux répond 200 puis 206 avec un
    // `Content-Range` correct, `Accept-Ranges: bytes`, et les fichiers sont
    // bien du H.264/AAC yuv420p avec l'atome `moov` en tête (lecture
    // progressive possible). Le refus vient donc du LECTEUR, et le code se
    // condamnait lui-même de deux façons :
    //
    //   1. Une seule secousse (`error`) suffisait : au bout de 8 s, si le
    //      `readyState` n'avait pas atteint 2, on basculait définitivement en
    //      « Lecture impossible » — même quand `video.error` était VIDE, donc
    //      quand le navigateur n'avait jamais renoncé. Sur un fichier de
    //      355 Mo et une connexion ordinaire, c'est vite arrivé.
    //   2. Aucune tentative de récupération : on n'essayait jamais un
    //      `.load()` avant de rendre les armes.
    //
    // Nouveau comportement : une relance automatique, puis on n'abandonne que
    // si le navigateur a réellement renoncé — et on affiche ALORS la cause.
    const v = videoRef.current;
    const media = v && v.error ? v.error : null;
    if (data?.video?.ai_generated) {
      setVideoError("Lecture impossible dans le navigateur");
      setVideoErrorDetail(_mediaErrorLabel(media));
      setIsBuffering(false);
      return;
    }
    setIsBuffering(false);
    if (!videoRecoveryTriedRef.current) {
      // Première secousse : on retente une fois, sans rien afficher.
      videoRecoveryTriedRef.current = true;
      setTimeout(() => {
        const el = videoRef.current;
        if (!el || el.readyState >= 2) return;
        try { el.load(); } catch { /* ignoré */ }
      }, 1200);
    }
    setTimeout(() => {
      const el = videoRef.current;
      if (el && el.readyState >= 2) return;          // lit finalement : on se taît
      if (el && !el.error && el.networkState === 2) return;  // encore en train de charger
      setVideoError("Lecture impossible dans le navigateur");
      setVideoErrorDetail(_mediaErrorLabel(el && el.error ? el.error : media));
    }, 12000);
  };

  // ── Progress bar ──
  const handleProgressClick = (e) => {
    if (!progressRef.current || !duration) return;
    const rect = progressRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    seekTo(pct * duration);
  };

  const handleProgressHover = (e) => {
    if (!progressRef.current || !duration) return;
    const rect = progressRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    setHoverTime(pct * duration);
    setHoverX(e.clientX - rect.left);
  };

  // ── Speaker map ──
  // Vincent B897 (Eric 2026-02-14 SPEC STRICK PROD) — La légende
  // (chips de couleur sous « Source de la transcription : ») affichait
  // TOUJOURS les locuteurs de Whisper (`data.segments`), même quand
  // Vincent avait basculé la source sur Fireflies ou Fusionnée. Fix :
  // on choisit dynamiquement la liste de segments source (Whisper /
  // Fireflies / Fusionnée) selon `transcriptSubTab`, avec fallback
  // Whisper si la source sélectionnée n'existe pas encore.
  //
  // Vincent B901 (Eric 2026-02-14 SPEC STRICK PROD) — Enrichit la
  // légende avec un compteur « N tours · X,X min » par locuteur pour
  // que Vincent voit la part de parole de chaque personne en un coup
  // d'œil. Calculé au même endroit que `speakerMap` pour ne pas re-
  // parcourir les segments 2 fois.
  const { speakerMap, speakerStats } = useMemo(() => {
    let segs = null;
    if (transcriptSubTab === "fireflies") {
      segs = allTranscripts?.fireflies?.segments || null;
    } else if (transcriptSubTab === "merged") {
      segs = allTranscripts?.merged?.segments || null;
    }
    if (!segs || segs.length === 0) {
      segs = data?.segments || [];
    }
    const map = {};
    const stats = {}; // { [displayName]: { turns, seconds } }
    // Vincent B934 (Eric 2026-02-14 SPEC STRICK PROD) — Table pour
    // regrouper les variantes du même locuteur (NBSP, accents NFC/NFD,
    // casse). Clef = nom normalisé → displayName (première variante
    // rencontrée).
    const keyToDisplay = {};
    let idx = 0;
    for (const seg of segs) {
      const name = (seg.speaker || "").trim();
      if (!name) continue;
      const key = normalizeSpeakerKey(name);
      let display = keyToDisplay[key];
      if (!display) {
        display = name;
        keyToDisplay[key] = display;
        map[display] = SPEAKER_COLORS[idx % SPEAKER_COLORS.length];
        idx++;
        stats[display] = { turns: 0, seconds: 0 };
      }
      stats[display].turns += 1;
      const dur = Math.max(0, (Number(seg.end) || 0) - (Number(seg.start) || 0));
      stats[display].seconds += dur;
    }
    return { speakerMap: map, speakerStats: stats };
  }, [transcriptSubTab, data?.segments, allTranscripts?.fireflies?.segments, allTranscripts?.merged?.segments]);

  // Vincent B921 (Eric 2026-02-14 SPEC STRICK PROD) — Tableau unifié
  // multi-source. On calcule une table `speakerStatsBySource` qui unit
  // les locuteurs des 3 sources (Whisper / Fireflies / Fusionnée) et
  // conserve, pour chacun, les métriques (tours + durée) par source.
  // Utilisé par `SpeakerLegendBox` (B920) pour montrer un tableau
  // Locuteur × Source. Le speakerColorMap est stable entre les sources
  // (même couleur pour un locuteur qu'il apparaisse dans une source ou
  // dans les 3), pour permettre à Vincent de comparer les 3
  // transcriptions sans confusion visuelle.
  // Tâche 1397 (Eric 2026-09-29) — Le badge « ⚠ » a besoin de savoir quelles
  // sources EXISTENT, pas seulement ce qu'elles disent de chaque locuteur :
  // une source jamais générée (« Fusionnée » absente) affichait « 100 % de
  // divergence », alors qu'il n'y a rien à comparer. Et il compte désormais
  // les MOTS en plus des secondes : deux diariseurs découpent les tours
  // différemment, le nombre de tours ne mesure pas un désaccord.
  // Tâche 1397 — Seuil d'alerte du badge, réglé par l'organisation.
  const [seuilDivergencePct, setSeuilDivergencePct] = useState(50);
  useEffect(() => {
    axios
      .get(`${API}/api/organizations/divergence-threshold`, { withCredentials: true })
      .then(({ data: d }) => setSeuilDivergencePct(d?.threshold_pct ?? 50))
      .catch(() => {});
  }, []);

  const enregistrerSeuil = useCallback(async (pct) => {
    try {
      const { data: d } = await axios.put(
        `${API}/api/organizations/divergence-threshold`,
        { threshold_pct: pct },
        { withCredentials: true },
      );
      setSeuilDivergencePct(d.threshold_pct);
      toast.success(tr(`Seuil réglé à ${d.threshold_pct} %`, `Threshold set to ${d.threshold_pct}%`));
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Enregistrement impossible", "Could not save"));
    }
  }, [tr]);

  const { speakerColorMap, speakerStatsBySource, sourcesPresentes, segmentsBySource } = useMemo(() => {
    const sources = {
      whisper: data?.segments || [],
      fireflies: allTranscripts?.fireflies?.segments || [],
      merged: allTranscripts?.merged?.segments || [],
    };
    const statsBySource = {}; // { [displayName]: { whisper: {...}, fireflies: {...}, merged: {...} } }
    const nameOrder = []; // stable insertion order (Whisper first, then Fireflies, then Merged)
    // Vincent B934 (Eric 2026-02-14 SPEC STRICK PROD) — Table de fusion :
    // les variantes du MÊME locuteur (NBSP, accents NFC/NFD, casse)
    // partagent la même entrée. Clef normalisée → displayName (première
    // variante rencontrée, préservée pour l'UI).
    const keyToDisplay = {};
    for (const src of ["whisper", "fireflies", "merged"]) {
      for (const seg of (sources[src] || [])) {
        const name = (seg.speaker || "").trim();
        if (!name) continue;
        const key = normalizeSpeakerKey(name);
        let display = keyToDisplay[key];
        if (!display) {
          display = name;
          keyToDisplay[key] = display;
          statsBySource[display] = {
            whisper: { turns: 0, seconds: 0, words: 0 },
            fireflies: { turns: 0, seconds: 0, words: 0 },
            merged: { turns: 0, seconds: 0, words: 0 },
          };
          nameOrder.push(display);
        }
        statsBySource[display][src].turns += 1;
        const dur = Math.max(0, (Number(seg.end) || 0) - (Number(seg.start) || 0));
        statsBySource[display][src].seconds += dur;
        statsBySource[display][src].words += ((seg.text || "").trim().match(/\S+/g) || []).length;
      }
    }
    const colorMap = {};
    nameOrder.forEach((n, i) => {
      colorMap[n] = SPEAKER_COLORS[i % SPEAKER_COLORS.length];
    });
    return {
      speakerColorMap: colorMap,
      speakerStatsBySource: statsBySource,
      sourcesPresentes: {
        whisper: (sources.whisper || []).length > 0,
        fireflies: (sources.fireflies || []).length > 0,
        merged: (sources.merged || []).length > 0,
      },
      // Tâche 1398 — La comparaison côte à côte relit les tours bruts.
      segmentsBySource: sources,
    };
  }, [data?.segments, allTranscripts?.fireflies?.segments, allTranscripts?.merged?.segments]);

  // Vincent B920 (Eric 2026-02-14 SPEC STRICK PROD) — État `collapsed`
  // de la boîte des locuteurs. Vit dans VideoDetailPage (parent des 3
  // sous-onglets) → conserve la position ouvert/fermé quand Vincent
  // bascule entre Whisper / Fireflies / Fusionnée.
  const [speakerLegendCollapsed, setSpeakerLegendCollapsed] = useState(false);

  // Vincent B923 (Eric 2026-02-14 SPEC STRICK PROD) — Ouverture du
  // TagsManagerDialog déclenchée au clic sur la chip « ÉTIQUETTES »
  // dans le header. Interface reste épurée : pas de bouton toujours
  // visible, tout passe par le clic sur la chip.
  const [tagsManagerOpen, setTagsManagerOpen] = useState(false);

  // Vincent B694 (Eric 2026-02-XX) → B696 (récurrence 2026-02-08) —
  // Options du menu déroulant pour changer le locuteur d'UNE phrase depuis
  // l'onglet Whisper. Sources par priorité :
  //   1. Noms mappés depuis l'onglet Locuteurs (`speakerMapping.values()`
  //      sans les labels génériques Speaker A/B/…)
  //   2. Locuteurs cochés « présents » (`presentSpeakers`)
  //   3. FALLBACK B696 : tous les locuteurs de la BANQUE de l'org
  //      (`speakerData.known_speakers`) — permet à Vincent d'attribuer un
  //      locuteur SANS avoir à faire d'abord la validation dans l'onglet
  //      Locuteurs. Backend whitelist accepte déjà ces noms.
  // Trié FR, dédupliqué, JAMAIS vide si la banque de locuteurs contient au
  // moins une entrée.
  const speakerOptions = useMemo(() => {
    const set = new Set();
    Object.values(speakerMapping || {}).forEach((n) => {
      if (n && !/^Speaker\s/i.test(String(n))) set.add(n);
    });
    (presentSpeakers instanceof Set ? Array.from(presentSpeakers) : []).forEach((n) => {
      if (n) set.add(n);
    });
    // B696 fallback — banque org complète
    (speakerData?.known_speakers || []).forEach((ks) => {
      if (ks?.name) set.add(ks.name);
    });
    return Array.from(set).sort((a, b) =>
      String(a).localeCompare(String(b), "fr", { sensitivity: "base" }),
    );
  }, [speakerMapping, presentSpeakers, speakerData?.known_speakers]);

  // Memoised active segment index: re-computes on every currentTime tick
  // but the returned primitive only changes when crossing a segment boundary,
  // so the auto-scroll effect below fires once per real segment change (not 4x/sec).
  const activeIdx = useMemo(() => {
    if (!data?.segments) return -1;
    return data.segments.findIndex((seg) => currentTime >= seg.start && currentTime <= seg.end);
  }, [data?.segments, currentTime]);

  // ── Auto-scroll transcript: fire ONLY when active segment changes,
  //    AND only if the active line is currently out of view. This kills the
  //    "up-down yo-yo" caused by smooth-scroll being re-triggered every 250ms.
  useEffect(() => {
    if (activeIdx < 0) return;
    const container = transcriptContainerRef.current;
    const el = activeSegmentRef.current;
    if (!container || !el) return;
    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    const margin = 40;
    const fullyVisible = eRect.top >= cRect.top + margin && eRect.bottom <= cRect.bottom - margin;
    if (fullyVisible) return;
    const top = el.offsetTop - container.offsetTop - container.clientHeight / 3;
    container.scrollTo({ top, behavior: "smooth" });
  }, [activeIdx]);

  // Memoise the streaming URL once per videoId. If we recompute it on every
  // render React swaps the <source src> attribute, which removes the loading
  // media from the document and throws "AbortError: media was removed from the
  // document" — leaving networkState=NETWORK_NO_SOURCE and a permanently broken
  // playback. Reading the JWT once at mount-time keeps the URL stable.
  // Vincent P1.10 (Eric 2026-07-31 SPEC STRICK PROD) — Le `<video>` HTML5
  // envoie automatiquement le cookie httpOnly en same-origin (ingress
  // Kubernetes) ; on n'a plus besoin de passer le JWT en query param.
  // Tâche 1210 (Eric 2026-06-26) — L'URL porte désormais un jeton de lecture
  // limité à cette vidéo (`vt`) au lieu de s'appuyer sur la session entière.
  // Le hook la calcule une seule fois par vidéo et retombe sur l'URL nue si
  // l'émission du jeton échoue.
  const streamUrl = useStreamToken({ videoId, api: API });
  const getTokenUrl = () => streamUrl;

  // Bogue 1218 (Eric 2026-06-26) — Ce rappel était créé EN LIGNE dans le JSX
  // de `KnowledgeExtractionProgress`. Son identité changeait donc à chaque
  // rendu, l'effet de polling de la carte (dépendance `onRetryStarted`) se
  // remontait à chaque rendu et son premier tick est immédiat → loadVideoData
  // → nouveau rendu → nouveau tick. D'où le clignotement de la page et la
  // rafale de requêtes pendant la génération des connaissances.
  const handleKnowledgeRefresh = useCallback(() => {
    // Petite pause puis reload du video doc pour capter l'état "running"
    // du pipeline dans le tracker.
    setTimeout(() => loadVideoData?.(), 800);
    startPolling?.();
  }, [loadVideoData, startPolling]);

  // Enhancement B621.b (Eric 2026-07-30 SPEC STRICK) — Navigation prev/next
  // dans KnowledgeDetailModal. Calcule les siblings du KU ouvert (mêmes type
  // + même vidéo) et expose 2 callbacks + un label « N / M ». Permet à
  // l'apprenant de feuilleter la liste (par ex. 35 Q&R) sans devoir fermer
  // et rouvrir le modal à chaque item. Actif SEULEMENT quand un KU est
  // ouvert dans le modal.
  const openScriptInModalRef = useRef(null);
  const openScriptInModal = useCallback(async (scriptDoc) => {
    if (!scriptDoc || !scriptDoc.id) return;
    // Eric (2026-02-10) — Convertit un script (`sales_scripts` doc) en KU
    // shape acceptée par `KnowledgeDetailModal`. Le flag `__isScriptDoc`
    // permet à `detailModalNav` d'utiliser la liste `scriptsListForModal`
    // (au lieu de `knowledge_units`) pour calculer siblings/label.
    // Éviter l'édition : on n'envoie pas isAdmin=true côté VideoDetailModals
    // (voir override plus bas).
    const parseTc = (tc) => {
      if (!tc) return 0;
      const m = String(tc).match(/\[?(\d{1,2}):(\d{2})(?::(\d{2}))?\]?/);
      if (!m) return 0;
      const h = m[3] ? parseInt(m[1], 10) : 0;
      const min = m[3] ? parseInt(m[2], 10) : parseInt(m[1], 10);
      const sec = m[3] ? parseInt(m[3], 10) : parseInt(m[2], 10);
      return h * 3600 + min * 60 + sec;
    };
    const toKu = (doc) => {
      const start = parseTc(doc.start_time);
      return {
        id: doc.id,
        org_id: doc.org_id,
        video_id: doc.video_id,
        type: "script",
        title: doc.title || tr("Script de vente", "Sales script"),
        status: doc.status || "pending_review",
        timestamps: { start, end: start + 60 },
        content: {
          scenario: doc.scenario || doc.contexte || "",
          v1_original: doc.v1_original || [],
          v2_reformule: doc.v2_reformule || [],
          v3_justifie: doc.v3_justifie || [],
          validation_table: doc.validation_table || [],
          // Bogue 1174 — Filet pour les scripts sans versions V1/V2/V3 : sans
          // ce champ, la vue de repli affichait « Texte du script » vide.
          text: doc.script_text || doc.text || doc.content || "",
        },
        __isScriptDoc: true,
      };
    };
    // Bogue 1174 (Eric 2026-06-25) — « Suivant / Précédent » n'affichait que le
    // titre. Cause : `GET /api/videos/{id}/scripts` PROJETTE HORS
    // `v1_original / v2_reformule / v3_justifie / validation_table` (liste
    // légère). Le clic sur une carte allait chercher le document complet via
    // `GET /api/scripts/{id}` — la navigation, elle, réutilisait l'entrée
    // légère de la liste. D'où un corps vide.
    const hasBody = Boolean(
      (scriptDoc.v1_original || []).length
      || (scriptDoc.v2_reformule || []).length
      || (scriptDoc.v3_justifie || []).length,
    );
    // Affichage immédiat (titre + compteur N/M) pour que le clic réponde tout
    // de suite, puis remplacement par le document complet.
    setDetailModalKu(toKu(scriptDoc));
    if (hasBody) return;
    try {
      const { data } = await axios.get(`${API}/api/scripts/${scriptDoc.id}`, { withCredentials: true });
      if (data && data.id) setDetailModalKu(toKu(data));
    } catch (e) {
      toast.error(tr(
        "Impossible de charger le contenu de ce script",
        "Could not load this script's content",
      ));
    }
  }, []);
  openScriptInModalRef.current = openScriptInModal;

  const detailModalNav = useMemo(() => {
    if (!detailModalKu) {
      return { prev: null, next: null, label: null };
    }
    // Eric (2026-02-10) — Filet Prev/Next Scripts : les scripts vivent dans
    // `sales_scripts`, pas dans `knowledge_units`. Quand un script est ouvert
    // dans la modale (`__isScriptDoc=true`), on utilise `scriptsListForModal`
    // (publié par `ScriptsPage embedded`) comme liste de siblings.
    if (detailModalKu.__isScriptDoc) {
      const idx = scriptsListForModal.findIndex((s) => s.id === detailModalKu.id);
      if (idx < 0 || scriptsListForModal.length <= 1) return { prev: null, next: null, label: null };
      return {
        prev: idx > 0 ? () => openScriptInModalRef.current?.(scriptsListForModal[idx - 1]) : null,
        next: idx < scriptsListForModal.length - 1 ? () => openScriptInModalRef.current?.(scriptsListForModal[idx + 1]) : null,
        label: `${idx + 1} / ${scriptsListForModal.length}`,
      };
    }
    if (!data?.knowledge_units?.length) {
      return { prev: null, next: null, label: null };
    }
    // « Siblings » = KUs du même type (avec les variantes canoniques
    // acceptées : procedure|process, script|sales_script|sales-script,
    // objection|rebuttal — d'anciennes vidéos ont des KUs stockés avec les
    // noms legacy du 1er pipeline. Sans les fusionner, Prev/Next affiche
    // "1 / 2" au lieu de "1 / 5" quand la vidéo mixe les 2 conventions.
    // Bogue signalé par Vincent 2026-02-10 (B699 résiduel).
    const currentType = detailModalKu.type;
    const canonicalTypes = (() => {
      if (currentType === "procedure" || currentType === "process") return ["procedure", "process"];
      if (currentType === "script" || currentType === "sales_script" || currentType === "sales-script") return ["script", "sales_script", "sales-script"];
      if (currentType === "objection" || currentType === "rebuttal") return ["objection", "rebuttal"];
      return [currentType];
    })();
    // Trier par timestamp croissant pour matcher l'ordre d'affichage
    // du panneau Objections (`.sort((a,b) => a.timestamp - b.timestamp)`) —
    // sans ce sort, Prev/Next sautait en désordre (ordre Mongo d'insertion).
    // Les autres panneaux (Q&R, Procédures, Concepts) n'ont pas de sort
    // côté frontend, donc ce tri ne modifie pas leur ordre visuel.
    const getStart = (u) => {
      const t = typeof u?.timestamp === "number" ? u.timestamp : (u?.timestamps?.start || 0);
      return Number.isFinite(t) ? t : 0;
    };
    const siblings = data.knowledge_units
      .filter((u) => canonicalTypes.includes(u.type))
      .sort((a, b) => getStart(a) - getStart(b));
    const idx = siblings.findIndex((u) => u.id === detailModalKu.id);
    if (idx < 0) return { prev: null, next: null, label: null };
    return {
      prev: idx > 0 ? () => setDetailModalKu(siblings[idx - 1]) : null,
      next: idx < siblings.length - 1 ? () => setDetailModalKu(siblings[idx + 1]) : null,
      label: `${idx + 1} / ${siblings.length}`,
    };
  }, [detailModalKu, data, scriptsListForModal]);

  // Poll the merged transcript until it appears or we hit max attempts.
  // GPT-5.2 fusion typically takes 5–60s; we poll every 5s for up to 3 minutes,
  // then give up gracefully (the user can still click "Rafraichir maintenant"
  // on the Fusionnée tab to retry manually).
  // pollMergedTranscript migré dans hooks/useTranscripts.js (Vincent B681)

  // ── Share functions ── (Vincent B680 — extraits dans hooks/useShareLinks.js)

  // ── Filtered segments (search) ──
  const filteredSegments = data?.segments?.filter((seg) => {
    if (!transcriptSearch) return true;
    return seg.text?.toLowerCase().includes(transcriptSearch.toLowerCase()) ||
      seg.speaker?.toLowerCase().includes(transcriptSearch.toLowerCase());
  }) || [];

  // ── Knowledge markers for timeline ──
  // Vincent B757 (Eric 2026-02-17) — Bug diagnostic Vincent : les traits
  // verticaux n'affichaient QUE les procédures/scripts, jamais les Q&R ni
  // les concepts. Causes :
  //   1. Q&R utilise `timestamps.anchor` (pas `.start`) — le filtre le
  //      rejetait silencieusement.
  //   2. `u.timestamps?.start && duration > 0` filtrait aussi les KUs à
  //      start=0 (début exact de vidéo) car 0 est falsy en JS.
  // Fix : on accepte `.start` OU `.anchor` (fallback), et on compare
  // explicitement avec `!= null` pour laisser passer les 0.
  const knowledgeMarkers = (data?.knowledge_units || [])
    .map((u) => {
      const ts = u.timestamps || {};
      const start = ts.start != null ? ts.start : ts.anchor;
      return start != null && duration > 0
        ? { ...u, pct: (start / duration) * 100 }
        : null;
    })
    .filter(Boolean);

  // ── Loading ──
  if (loading) {
    return <VideoLoadingState />;
  }

  if (!data?.video) {
    return <VideoNotFoundState />;
  }

  const { video, transcript, segments, knowledge_units } = data;
  // Vincent #304 (2026-06-12) — Document textuel : pas de vidéo, pas de
  // transcript Whisper, pas de locuteurs. On masque les 2 onglets concernés
  // et on affiche le contenu textuel à la place du lecteur vidéo.
  const isTextDoc = video?.is_text_doc === true;

  // Tâche 1468 (Eric 2026-06) — Les fichiers texte n'avaient AUCUN
  // « Précédent / Suivant » : `/api/videos` ne les renvoie pas, la liste des
  // frères était donc vide et la navigation disparaissait entièrement.
  // ⚠️ Les deux listes restent SÉPARÉES : « 7 / 224 » doit compter les
  // fichiers texte, pas les vidéos — mélanger les deux ferait passer d'un
  // .docx à une vidéo sans qu'on l'ait demandé.
  // ⚠️ Le réglage PAR FICHIER gagne sur celui de l'organisation : sinon un
  // document sensible resterait téléchargeable parce que le global est ouvert.
  const telechargementBloque = video?.download_blocked != null
    ? !!video.download_blocked
    : !!politiqueTelechargement.bloque;

  const basculerTelechargement = async (bloquer) => {
    try {
      await axios.patch(`${API}/api/text-documents/${videoId}`,
        { download_blocked: bloquer }, { withCredentials: true });
      await loadVideoData();
      toast.success(bloquer
        ? tr("Téléchargement désactivé pour ce document.",
             "Download disabled for this document.")
        : tr("Téléchargement permis pour ce document.",
             "Download allowed for this document."));
    } catch (err) {
      toast.error(err?.response?.data?.detail
        || tr("Réglage impossible.", "Could not change the setting."));
    }
  };

  const freres = isTextDoc ? siblingDocs : siblingVideos;
  const _siblingIdx = freres.findIndex((v) => v.id === videoId);
  const _hasPrevVideo = _siblingIdx > 0;
  const _hasNextVideo = _siblingIdx >= 0 && _siblingIdx < freres.length - 1;
  const goToPrevVideo = _hasPrevVideo
    ? () => navigate(`/dashboard/videos/${freres[_siblingIdx - 1].id}`)
    : null;
  const goToNextVideo = _hasNextVideo
    ? () => navigate(`/dashboard/videos/${freres[_siblingIdx + 1].id}`)
    : null;
  const videoNavLabel = _siblingIdx >= 0 ? `${_siblingIdx + 1} / ${freres.length}` : "";
  // Vincent B718 bonus (Eric 2026-02-11) — Titres pour tooltips sur les
  // boutons Prev/Next dans le header (aperçu de la destination).
  const prevVideoTitle = _hasPrevVideo ? (freres[_siblingIdx - 1].title || "") : "";
  const nextVideoTitle = _hasNextVideo ? (freres[_siblingIdx + 1].title || "") : "";
  const isProcessing = video.pipeline_status === "processing" || video.pipeline_status === "pending";
  const isFailed = video.pipeline_status === "failed";
  const isComplete = video.pipeline_status === "complete";
  // Vincent B919 (Eric 2026-02-14 SPEC STRICK PROD) — Régression PROD :
  // « un pipeline en pause avant la fusion s'affiche quand même comme
  // s'il travaillait encore ». Le badge d'état dans l'en-tête ne
  // connaissait que 3 états (complete / failed / fallback = En cours
  // avec spinner). Les pauses (`paused_pending_fusion`,
  // `paused_pending_extraction`, `paused_pending_user`) tombaient dans
  // le fallback → spinner qui tourne alors que le pipeline attend une
  // action manuelle. Fix : dériver un flag `isPaused` et l'envoyer au
  // header pour afficher un badge distinct (ambre + icône warning).
  const isPaused = typeof video.pipeline_status === "string"
    && video.pipeline_status.startsWith("paused_");
  // Vincent's spec (30-avril-2026): block Fireflies upload + fusion until Whisper
  // AND speaker diarization are done — otherwise the pipeline crashes when trying
  // to align two transcripts on a partial Whisper output. Mirror the backend
  // PIPELINE_READY_STAGES constant in /app/backend/routers/videos.py.
  //
  // Vincent (2026-02) bug #529 — Ajout des états de PAUSE post-Whisper :
  //   `awaiting_fusion`, `awaiting_extraction`, `fusion_running` (stages)
  //   `paused_pending_fusion`, `paused_pending_extraction` (statuts)
  // Pendant ces pauses, Whisper EST terminé (c'est la raison de la pause) —
  // il faut donc permettre l'upload Fireflies. L'ancien message d'erreur
  // « Patientez : Whisper doit être terminé » était incorrect et bloquant.
  const _readyStages = [
    "transcription_complete", "diarization_complete",
    "awaiting_fusion", "awaiting_extraction", "fusion_running",
    "extraction", "embedding", "complete",
  ];
  const _pausedStatuses = ["paused_pending_fusion", "paused_pending_extraction"];
  const fusionAvailable = isComplete
    || _readyStages.includes(video.pipeline_stage)
    || _pausedStatuses.includes(video.pipeline_status);
  const fusionBlockedReason = fusionAvailable
    ? null
    : isFailed
    ? (lang === "fr"
        ? "La transcription Whisper a echoue. Relancez le pipeline avant la fusion."
        : "Whisper transcription failed. Re-run the pipeline before merging.")
    : (lang === "fr"
        ? "Patientez : la transcription Whisper doit être terminée avant la fusion (l'import Fireflies reste possible en parallèle)."
        : "Please wait: Whisper must finish before merging (Fireflies import stays available in parallel).");
  // Vincent B751 (Eric 2026-02-17) — L'IMPORT Fireflies (via
  // /cross-validate-transcript-file?defer_fusion=true) fonctionne EN
  // PARALLÈLE de Whisper depuis longtemps. On ne bloque désormais l'import
  // QUE quand le pipeline a explicitement échoué. La fusion reste gouvernée
  // par fusionBlockedReason (contrôle sur les boutons « Refaire fusion »).
  const importBlockedReason = isFailed
    ? (lang === "fr"
        ? "La transcription Whisper a échoué. Relancez le pipeline avant l'import Fireflies."
        : "Whisper transcription failed. Re-run the pipeline before importing Fireflies.")
    : null;
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Vincent XLSX 2026-07-15 Rows 485 + 486 (Eric 2026-07-16 SPEC STRICK) —
  // Le pipeline est considéré comme « déjà lancé au moins une fois » dès que :
  //   • une transcription fusionnée existe (has_merged), OU
  //   • le status vidéo est "complete" (pipeline entièrement terminé), OU
  //   • Whisper a produit des segments ET les locuteurs sont validés (diarization_done)
  // Utilisé pour masquer « Pipeline non lancé » et le bouton « Relancer » dans
  // les onglets Q&R / Scripts / Procédures / Objections.
  const pipelineHasEverRun = !!(
    allTranscripts?.merged?.full_text ||
    allTranscripts?.summary?.has_merged ||
    isComplete ||
    (allTranscripts?.summary?.has_whisper && video?.speaker_validation === "validated")
  );

  // Vincent #304 — Pour un document textuel, l'onglet par défaut doit être
  // "qa" (Q&R) puisque Locuteurs/Transcriptions n'existent pas.
  if (isTextDoc && (activePanel === "speakers" || activePanel === "transcript")) {
    setTimeout(() => setActivePanel("qa"), 0);
  }

  // Tâche 1310 (Eric 2026-06-30) — La source canonique s'affiche dans
  // l'en-tête (chip « SOURCE »), plus sous les boutons de l'onglet
  // Transcriptions. Repli historique : fusionnée si elle existe, sinon
  // Whisper — identique à celui du sélecteur de source.
  const canonicalSourceLabel = (() => {
    // Tâche 1465 — Un fichier texte n'a pas de « source canonique » : il n'y a
    // qu'un seul texte, celui du fichier. Afficher « Whisper » nommait un
    // moteur de transcription audio qui n'a jamais touché ce document.
    if (isTextDoc) {
      return libelleTypeFichier(video?.original_filename, video?.source_kind, lang);
    }
    const canon = video?.canonical_transcript_source
      || (allTranscripts?.summary?.has_merged ? "merged" : "whisper");
    if (canon === "merged") return lang === "fr" ? "fusionnée" : "merged";
    if (canon === "fireflies") return "Fireflies";
    if (canon === "whisper") return "Whisper";
    return canon;
  })();

  return (
    <div className="animate-fade-in" data-testid="video-detail-page">
      {/* Eric (2026-06-14) — Barre de progression visuelle des jobs IA d'arrière-plan.
          Eric 2026-07-20 (iter162) — Extraite dans AIJobProgressBar.js. */}
      <AIJobProgressBar aiJobProgress={aiJobProgress} lang={lang} />

      {/* Vincent B1025 (Eric 2026-02-26 SPEC STRICK PROD) — Bandeau
          « Vidéo retirée de la file d'attente ». Visible uniquement si
          l'admin l'a détachée volontairement via VideoQueuePage. Bouton
          « Remettre dans la file » qui appelle POST /videos/{id}/queue/reattach
          puis recharge la vidéo. Ne bloque aucune fonctionnalité (les Q&R,
          scripts, procédures restent consultables). */}
      {canEditKnowledge && video?.queue_detached && (
        <div
          className="mb-4 rounded-xl border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 flex items-start justify-between gap-4"
          data-testid="video-queue-detached-banner"
        >
          <div className="text-sm text-amber-900 dark:text-amber-200">
            <div className="font-semibold mb-0.5">
              {tr("Vidéo retirée de la file d'attente", "Video removed from the queue")}
              {video.queue_detached_at
                ? ` — ${new Date(video.queue_detached_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA")}`
                : ""}
              {video.queue_detached_by ? tr(" par ", " by ") + video.queue_detached_by : ""}
            </div>
            <div className="text-xs opacity-90">
              {tr(
                "Le pipeline est arrêté pour cette vidéo. Toutes ses données restent accessibles. Vous pouvez la remettre dans la file pour relancer un traitement.",
                "The pipeline is stopped for this video. All of its data stays available. You can put it back in the queue to run processing again.",
              )}
            </div>
          </div>
          <button
            onClick={async () => {
              try {
                await axios.post(
                  `${API}/api/videos/${videoId}/queue/reattach`,
                  {},
                  { withCredentials: true },
                );
                toast.success(tr("Vidéo remise dans la file d'attente.", "Video put back in the queue."));
                await loadVideoData();
              } catch (e) {
                toast.error(e?.response?.data?.detail
                  || tr("Impossible de remettre la vidéo dans la file", "Could not put the video back in the queue"));
              }
            }}
            className="shrink-0 px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white text-xs font-semibold rounded-lg"
            data-testid="video-queue-reattach-btn"
          >
            {tr("Remettre dans la file", "Put back in the queue")}
          </button>
        </div>
      )}

      {/* ── Header ──
            Eric 2026-06-25 (iter125 Session A11) — Extrait dans
            VideoDetailHeader.js (-313 lignes). */}
      <VideoDetailHeader
        lang={lang}
        api={API}
        video={video}
        videoId={videoId}
        canEdit={canEditKnowledge}
        isAdmin={isAdmin}
        isComplete={isComplete}
        isFailed={isFailed}
        isPaused={isPaused}
        openTagsManager={() => setTagsManagerOpen(true)}
        canonicalSourceLabel={canonicalSourceLabel}
        isTextDoc={isTextDoc}
        telechargementBloque={telechargementBloque}
        peutReglerTelechargement={!!politiqueTelechargement.peut_regler}
        onBasculerTelechargement={basculerTelechargement}
        user={user}
        data={data}
        goBack={goBack}
        goToPrevVideo={goToPrevVideo}
        goToNextVideo={goToNextVideo}
        videoNavLabel={videoNavLabel}
        prevVideoTitle={prevVideoTitle}
        nextVideoTitle={nextVideoTitle}
        editMode={editMode}
        setEditMode={setEditMode}
        editSaving={editSaving}
        editTitle={editTitle}
        setEditTitle={setEditTitle}
        editTheme={editTheme}
        setEditTheme={setEditTheme}
        editAccessLevel={editAccessLevel}
        setEditAccessLevel={setEditAccessLevel}
        editDropbox={editDropbox}
        setEditDropbox={setEditDropbox}
        categories={categories}
        allPathways={allPathways}
        editCategoryId={editCategoryId}
        setEditCategoryId={setEditCategoryId}
        editSelectedPathwayIds={editSelectedPathwayIds}
        setEditSelectedPathwayIds={setEditSelectedPathwayIds}
        startEdit={startEdit}
        saveEdit={saveEdit}
        openShareModal={openShareModal}
        setImportOpen={setImportOpen}
        setShowDeleteConfirm={setShowDeleteConfirm}
        pathwaySuggestions={pathwaySuggestions}
        pathwayAdding={pathwayAdding}
        setPathwayAdding={setPathwayAdding}
        pathwaySelected={pathwaySelected}
        setPathwaySelected={setPathwaySelected}
        pathwayBannerDismissed={pathwayBannerDismissed}
        setPathwayBannerDismissed={setPathwayBannerDismissed}
        loadVideoData={loadVideoData}
      />

      {/* Vincent B902 (Eric 2026-02-14 SPEC STRICK PROD) — Bandeau
          tags suggérés (LLM + TF-IDF hybride) juste sous l'en-tête,
          visible sur toutes les vidéos/textes consultés depuis
          l'Explorateur. Accept/reject 1 par 1 ou en bulk. Les tags
          acceptés sont propagés côté back via l'endpoint + reload local. */}
      {canEditKnowledge && videoId && (
        <TagSuggestionsBar
          videoId={videoId}
          canEdit={canEditKnowledge}
          lang={lang}
          onAccepted={() => { if (typeof loadVideoData === "function") loadVideoData(); }}
          onRejected={() => { if (typeof loadVideoData === "function") loadVideoData(); }}
        />
      )}

      {/* Vincent B743 (Eric 2026-02-14) — Les 2 boîtes (📚 Jargon + 🔥 Fireflies)
          sont désormais rendues DANS la colonne gauche du splitter, JUSTE
          SOUS le player vidéo, EMPILÉES verticalement (Jargon d'abord, puis
          Fireflies). Ancienne architecture (B736) plaçait les 2 côte à côte
          (50/50) SOUS TOUT le header — Vincent voulait un vrai empilage sous
          la vidéo dans le mode côte-à-côte. */}
      {/* ── Ancien emplacement supprimé — voir plus bas au sein du splitter ── */}

      {/* ── Pipeline Progress + Failed status ──
            Eric 2026-06-25 (iter125 Session A12) — Extrait dans
            VideoPipelineStatus.js (-83 lignes).
            Vincent B550 (Eric 2026-07-24) — On passe aussi les 2 flags
            per-video pour que la barre horizontale affiche en rouge les
            étapes "diarization" / "facial_id" quand elles ont été bypassées
            pour cette vidéo précise (en plus du bypass global org). */}
      <VideoPipelineStatus
        lang={lang}
        api={API}
        video={video}
        videoId={videoId}
        isProcessing={isProcessing}
        isFailed={isFailed}
        pipelineStatus={pipelineStatus}
        startPolling={startPolling}
        loadVideoData={loadVideoData}
        pipelineFlags={pipelineFlags}
        videoUseDiarisation={useDiarisation}
        videoUseFacialId={useFacialId}
        canEdit={canEditKnowledge}
      />

      {/* B737 (Eric 2026-02-14) — Progression détaillée des 7 pipelines de
          connaissances (Q&R, Scripts, Procédures, Objections, Concepts,
          Dictionnaire, Synthèse). Sans ça, Vincent voit la vidéo « Complete »
          alors que Q&R + Procédures tournent encore 4 min → il croit qu'on
          les a oubliés. La carte se masque toute seule si l'utilisateur
          n'est pas admin/concepteur (canEditKnowledge) ou si le doc ne
          contient pas encore de tracker (vieilles vidéos pré-B737). */}
      <KnowledgeExtractionProgress
        progress={video?.knowledge_extraction_progress}
        canEdit={canEditKnowledge}
        lang={lang}
        api={API}
        videoId={videoId}
        onRetryStarted={handleKnowledgeRefresh}
      />

      {/* Eric 2026-08-05 (bogue 666 — rapport dans la fiche vidéo) — Bandeau
          collapsible affichant le rapport d'envoi persisté dans le document
          vidéo. Permet à Vincent de retrouver après coup la durée, le débit
          et les difficultés rencontrées lors de l'upload initial (utile
          pour le debug rétrospectif d'un pipeline lent ou d'une vidéo qui
          a eu des retries chunk). Affiché uniquement si le rapport existe. */}
      {video?.upload_report && (() => {
        const r = video.upload_report;
        const totalSec = Math.max(0, Math.round((r.total_ms || 0) / 1000));
        const mins = Math.floor(totalSec / 60);
        const secs = totalSec % 60;
        const durationStr = mins > 0 ? `${mins} min ${secs} s` : `${secs} s`;
        const mo = (r.file_size || 0) / (1024 * 1024);
        const throughput = totalSec > 0 ? (mo / totalSec).toFixed(1) : "—";
        const hasFailures = (r.total_retries || 0) > 0;
        const failureLines = Object.entries(r.failures_by_type || {})
          .sort((a, b) => b[1] - a[1])
          .map(([k, v]) => `${v} × ${k}`);
        const savedAt = r.saved_at ? new Date(r.saved_at).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", {
          day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
        }) : null;
        return (
          <details
            className={`my-3 rounded-lg border text-sm ${hasFailures
              ? "bg-amber-500/5 border-amber-500/30"
              : "bg-teal-500/5 border-teal-500/20"}`}
            data-testid="video-upload-report"
          >
            <summary className="cursor-pointer px-4 py-2 flex items-center justify-between gap-3 select-none">
              <span className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                  {lang === "fr" ? "Rapport d'envoi" : "Upload report"}
                </span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${hasFailures
                  ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
                  : "bg-teal-500/15 text-teal-700 dark:text-teal-300"}`}>
                  {hasFailures
                    ? (lang === "fr" ? "Difficultés rencontrées" : "Issues encountered")
                    : (lang === "fr" ? "Sans encombre" : "Smooth")}
                </span>
              </span>
              <span className="text-xs text-muted-foreground font-mono">
                {durationStr} · {throughput} Mo/s
              </span>
            </summary>
            <div className="px-4 pt-1 pb-3 border-t border-border/50">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 mt-2">
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {lang === "fr" ? "Durée totale" : "Total duration"}
                  </span>
                  <span className="font-semibold" data-testid="video-upload-report-duration">{durationStr}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {lang === "fr" ? "Débit moyen" : "Average throughput"}
                  </span>
                  <span className="font-semibold" data-testid="video-upload-report-throughput">{throughput} Mo/s</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {lang === "fr" ? "Taille" : "Size"}
                  </span>
                  <span className="font-semibold">
                    {r.file_size ? (r.file_size / (1024 * 1024)).toFixed(1) + " Mo" : "—"}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {lang === "fr" ? "Mode" : "Mode"}
                  </span>
                  <span className="font-semibold">
                    {r.mode === "chunked"
                      ? (lang === "fr"
                        ? `Chunké · ${r.total_chunks || 0} × 10 Mo${r.resumed ? " (repris)" : ""}`
                        : `Chunked · ${r.total_chunks || 0} × 10 Mo${r.resumed ? " (resumed)" : ""}`)
                      : (lang === "fr" ? "Direct" : "Direct")}
                  </span>
                </div>
              </div>
              {r.mode === "chunked" && (
                <div className="mt-3">
                  {hasFailures ? (
                    <>
                      <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 mb-1">
                        {lang === "fr"
                          ? `${r.chunks_with_retries || 0} morceau(x) sur ${r.total_chunks || 0} ont dû être renvoyés (${r.total_retries} tentative(s) au total).`
                          : `${r.chunks_with_retries || 0} of ${r.total_chunks || 0} chunks were retried (${r.total_retries} attempts in total).`}
                      </p>
                      <ul className="text-xs text-muted-foreground list-disc pl-5 space-y-0.5" data-testid="video-upload-report-difficulties">
                        {failureLines.map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      {lang === "fr"
                        ? `Aucune difficulté : les ${r.total_chunks || 0} morceaux sont passés du premier coup.`
                        : `No issues: all ${r.total_chunks || 0} chunks succeeded on first try.`}
                    </p>
                  )}
                </div>
              )}
              {savedAt && (
                <p className="text-[10px] text-muted-foreground/70 mt-3 italic">
                  {lang === "fr" ? "Enregistré" : "Saved"} · {savedAt}
                  {r.saved_by ? ` · ${r.saved_by}` : ""}
                </p>
              )}
            </div>
          </details>
        );
      })()}

      {/* ── Main Content: Video + Panel ──
          Vincent #97 (2026-05-12) — Splitter draggable sur lg+ pour permettre
          à chaque admin de redimensionner Vidéo ↔ Panneau onglets. Sur < lg,
          fallback en stack vertical (panneau sous vidéo). Les largeurs sont
          contrôlées par les CSS custom properties --left-pct / --right-pct,
          qui ne s'appliquent qu'au breakpoint lg via la classe lg:flex-row. */}
      <div
        ref={containerRef}
        className={`flex flex-col gap-4 ${isStacked ? "" : "lg:flex-row lg:gap-0"} video-split`}
        style={{ "--left-pct": `calc(${splitPct}% - 6px)`, "--right-pct": `calc(${100 - splitPct}% - 6px)` }}
        data-testid="video-panel-splitter"
      >
        {/* ── Video Player Area ──
            Eric 2026-06-25 (iter124 Session A9) — Extrait dans
            VideoPlayerArea.js (-353 lignes de VideoDetailPage.js). */}
        {/* Vincent B743 (Eric 2026-02-14) — Wrapper de la colonne gauche
            contenant : le player vidéo + les 2 boîtes Jargon/Fireflies
            empilées verticalement JUSTE en dessous. Le wrapper porte la
            largeur `--left-pct` du splitter en mode côte-à-côte ; en mode
            empilé (isStacked), il prend toute la largeur. */}
        <div
          className={`flex flex-col gap-3 min-w-0 w-full ${isStacked ? "" : "lg:[width:var(--left-pct)]"}`}
          data-testid="video-left-column"
        >
          <VideoPlayerArea
          isTextDoc={isTextDoc}
          isStacked={isStacked}
          lang={lang}
          api={API}
          video={video}
          videoRef={videoRef}
          playerContainerRef={playerContainerRef}
          progressRef={progressRef}
          isPlaying={isPlaying}
          setIsPlaying={setIsPlaying}
          isBuffering={isBuffering}
          setIsBuffering={setIsBuffering}
          isMuted={isMuted}
          volume={volume}
          playbackSpeed={playbackSpeed}
          showControls={showControls}
          controlsVisible={controlsVisible}
          setControlsVisible={setControlsVisible}
          showSpeedMenu={showSpeedMenu}
          setShowSpeedMenu={setShowSpeedMenu}
          showVolumeSlider={showVolumeSlider}
          setShowVolumeSlider={setShowVolumeSlider}
          setShowShortcutsHelp={setShowShortcutsHelp}
          currentTime={currentTime}
          duration={duration}
          hoverTime={hoverTime}
          setHoverTime={setHoverTime}
          hoverX={hoverX}
          videoError={videoError}
          setVideoError={setVideoError}
          videoErrorDetail={videoErrorDetail}
          setVideoErrorDetail={setVideoErrorDetail}
          knowledgeMarkers={knowledgeMarkers}
          knowledgeUnitsRaw={data?.knowledge_units || []}
          handleVideoError={handleVideoError}
          handleLoadedMetadata={handleLoadedMetadata}
          handleTimeUpdate={handleTimeUpdate}
          handleProgressClick={handleProgressClick}
          handleProgressHover={handleProgressHover}
          togglePlay={togglePlay}
          toggleMute={toggleMute}
          toggleFullscreen={toggleFullscreen}
          togglePiP={togglePiP}
          skip={skip}
          changeSpeed={changeSpeed}
          changeVolume={changeVolume}
          getTokenUrl={getTokenUrl}
          formatTime={formatTime}
          isProcessing={isProcessing}
          isComplete={isComplete}
          progress={progress}
          allTranscripts={allTranscripts}
          TYPE_COLORS={TYPE_COLORS}
          SPEED_OPTIONS={SPEED_OPTIONS}
          onLegendActivate={(type, focusUnitId) => {
            // Vincent B702 (Eric 2026-02-11) — Clic sur légende timeline OU
            // sur un marqueur de la progress-bar : ouvre le panneau
            // correspondant à droite + auto-scroll vers l'item ciblé.
            const TYPE_TO_PANEL = {
              qa: "qa",
              procedure: "procedures",
              script: "scripts",
              objection: "objections",
              rebuttal: "objections",
              concept: "knowledge",
              synthesis: "synthesis",
            };
            const target = TYPE_TO_PANEL[type];
            if (target) {
              setActivePanel(target);
              // Détermine l'ID cible : focusUnitId (clic marqueur) ou 1er
              // KU du type dans la liste (clic légende).
              let targetId = focusUnitId;
              if (!targetId) {
                const first = (data?.knowledge_units || []).find((u) => u.type === type);
                targetId = first?.id;
              }
              if (targetId) {
                setTimeout(() => {
                  const el = document.querySelector(`[data-focus-target="${targetId}"]`);
                  if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                    el.classList.add("ring-2", "ring-offset-2");
                    el.style.setProperty("--tw-ring-color", TYPE_COLORS[type]?.color || "#002FA7");
                    setTimeout(() => {
                      el.classList.remove("ring-2", "ring-offset-2");
                      el.style.removeProperty("--tw-ring-color");
                    }, 1500);
                  }
                }, 200);
              }
            }
          }}
        />
          {/* B743 (Eric 2026-02-14) — Jargon + Fireflies preview EMPILÉS
              verticalement, directement sous la vidéo. */}
          <JargonTermsPanel videoId={video?.id} canEdit={isAdmin} />
          {/* Vincent B890 (Eric 2026-02-14 SPEC STRICK PROD) — Rectangles
              admin (Debug fondateur + Corriger la vidéo) déplacés depuis
              SpeakersPanel vers ici pour rester visibles quel que soit
              l'onglet du panneau de droite (Transcriptions, Q&R, Scripts,
              etc.). L'ordre exigé par Vincent est : Debug 1er, Corriger
              2e, Fireflies 3e — le composant gère 1 et 2, la card
              Fireflies existante reste en dessous. */}
          <VideoActionsCards
            video={video}
            videoId={video?.id}
            isAdmin={isAdmin}
            isFounder={!!user?.is_founder}
            lang={lang}
            useDiarisation={useDiarisation}
            useFacialId={useFacialId}
            onDataChanged={loadVideoData}
          />
          {canEditKnowledge && allTranscripts?.summary?.has_fireflies && allTranscripts?.fireflies?.full_text && (
            <div
              className="rounded-lg border border-amber-300 bg-amber-50/60 p-3 text-sm"
              data-testid="fireflies-preview-card"
            >
              <div className="flex items-center justify-between mb-1.5">
                {/* Vincent B890 (Eric 2026-02-14 SPEC STRICK PROD) — Retire
                    `uppercase tracking-wider` : Vincent veut « Fireflies déjà
                    disponible » en casse normale, seul « Fireflies » garde
                    sa majuscule naturelle. */}
                <div className="inline-flex items-center gap-2 text-amber-900 font-semibold text-xs">
                  <span data-testid="fireflies-preview-icon">🔥</span>
                  {lang === "fr" ? "Fireflies déjà disponible" : "Fireflies already available"}
                  {!allTranscripts?.summary?.has_whisper && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 font-bold normal-case" data-testid="fireflies-preview-early-badge">
                      {lang === "fr" ? "avant Whisper" : "before Whisper"}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => {
                    setActivePanel("transcript");
                    setTranscriptSubTab("fireflies");
                    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
                  }}
                  className="text-[11px] text-amber-800 hover:text-amber-950 hover:underline font-semibold inline-flex items-center gap-1"
                  data-testid="fireflies-preview-open-btn"
                >
                  {lang === "fr" ? "Ouvrir l'onglet →" : "Open tab →"}
                </button>
              </div>
              <p
                className="text-[13px] text-amber-950/90 leading-relaxed italic line-clamp-3"
                data-testid="fireflies-preview-snippet"
                title={allTranscripts.fireflies.full_text.slice(0, 500)}
              >
                « {allTranscripts.fireflies.full_text.trim().slice(0, 200).trim()}
                {allTranscripts.fireflies.full_text.trim().length > 200 && "…"} »
              </p>
              {allTranscripts.fireflies.filename && (
                <div className="mt-1 text-[10px] text-amber-700 font-mono truncate" data-testid="fireflies-preview-filename">
                  {lang === "fr" ? "Source : " : "Source: "}{allTranscripts.fireflies.filename}
                </div>
              )}
            </div>
          )}
          {/* Tâche 1435, étape 2 (Eric 2026-10-02) — Carte « Source web » :
              l'URL aspirée, la date de la dernière aspiration, et le bouton
              de mise à jour manuelle demandé par Eric. Posée sous le contenu
              du document, là où le formateur lit ce qui a été aspiré. */}
          {isTextDoc && video?.source_kind === "url" && (
            <SourceWebCard video={video} onUpdated={loadVideoData} />
          )}

        </div>

        {/* ── Drag Handle (Vincent #97) — visible uniquement lg+ ──
            Mode côte-à-côte : poignée verticale entre vidéo et panneau.
            Mode empilé (splitPct ≥ 88%) : poignée horizontale pleine largeur
            sous la vidéo, avec un bouton "Revenir au mode côte-à-côte". */}
        <div
          className={isStacked
            ? "hidden lg:flex items-center justify-center cursor-col-resize w-full py-2 group select-none gap-3"
            : "hidden lg:flex items-center justify-center cursor-col-resize group select-none"}
          style={isStacked ? {} : { width: "12px" }}
          onMouseDown={(e) => { e.preventDefault(); setIsDragging(true); }}
          onTouchStart={(e) => { e.preventDefault(); setIsDragging(true); }}
          data-testid="video-panel-drag-handle"
          title={isStacked
            ? tr(
                "Glissez vers la gauche pour revenir au mode côte-à-côte",
                "Drag left to go back to the side-by-side view",
              )
            : tr(
                "Glissez pour redimensionner — glissez à droite pour passer en mode empilé",
                "Drag to resize — drag right to switch to the stacked view",
              )}
        >
          <div className={isStacked
            ? `h-1 w-32 rounded-full transition-all ${isDragging ? "bg-[#002FA7]" : "bg-zinc-300 group-hover:bg-[#002FA7]/60"}`
            : `w-1 h-16 rounded-full transition-all ${isDragging ? "bg-[#002FA7]" : "bg-zinc-300 group-hover:bg-[#002FA7]/60"}`} />
          {isStacked && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); resetSplit(); }}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              className="text-xs text-[#002FA7] hover:text-[#001f70] hover:underline font-medium cursor-pointer"
              data-testid="exit-stacked-mode-btn"
            >
              {tr("Mode côte-à-côte", "Side-by-side view")}
            </button>
          )}
        </div>

        {/* ── Side Panel ── */}
        <div className={`min-w-0 w-full ${isStacked ? "" : "lg:[width:var(--right-pct)]"}`}>
          <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden sticky top-20">
            {/* Vincent #118 (2026-05-11) — Nouvel ordre : Locuteurs · Transcriptions · Q&R · Scripts · Procédures · Connaissances (sans numéros).
                Vincent #304 (2026-06-12) — Pour un document textuel, on masque
                Locuteurs et Transcriptions (pas de vidéo, donc pas de diarisation
                ni de transcription Whisper à afficher). */}
            {/* Eric 2026-07-20 (iter162) — Extrait dans PanelTabsBar.js.
                Reçoit activePanel, callbacks lazy-load, et knowledge_units
                pour les bulles de comptage. */}
            <PanelTabsBar
              activePanel={activePanel}
              setActivePanel={setActivePanel}
              isTextDoc={isTextDoc}
              lang={lang}
              knowledgeUnits={knowledge_units}
              scriptsList={scriptsListForModal}
              scriptsCount={data?.scripts_count}
              speakerData={speakerData}
              loadSpeakerData={loadSpeakerData}
              allTranscripts={allTranscripts}
              transcriptsLoading={transcriptsLoading}
              loadAllTranscripts={loadAllTranscripts}
              favKuIds={favKuIds}
            />

            {/* Vincent B849 (Eric 2026-02-14 SPEC STRICK PROD) — Panneau
                « Mes favoris » qui agrège les ♥ marqués par l'utilisateur
                sur les 5 panneaux Connaissances de cette vidéo. */}
            <FavoritesTabPanel
              show={activePanel === "favorites"}
              lang={lang}
              knowledgeUnits={knowledge_units}
              scriptsList={scriptsListForModal}
              favKuIds={favKuIds}
              setDetailModalKu={setDetailModalKu}
            />

            {/* ── Transcript Panel ── */}
            {/* Vincent B683 (Eric 2026-02-XX) — Extrait dans TranscriptPanel.js
                (~340 lignes économisées sur VideoDetailPage.js). */}
            <TranscriptPanel
              show={activePanel === "transcript"}
              lang={lang}
              t={t}
              API={API}
              videoId={videoId}
              canEditKnowledge={canEditKnowledge}
              isAdmin={isAdmin}
              transcriptSearch={transcriptSearch}
              setTranscriptSearch={setTranscriptSearch}
              filteredSegments={filteredSegments}
              transcriptSubTab={transcriptSubTab}
              setTranscriptSubTab={setTranscriptSubTab}
              allTranscripts={allTranscripts}
              setAllTranscripts={setAllTranscripts}
              transcriptsLoading={transcriptsLoading}
              loadAllTranscripts={loadAllTranscripts}
              fusionBlockedReason={fusionBlockedReason}
              fusionAvailable={fusionAvailable}
              importBlockedReason={importBlockedReason}
              openReportModal={openReportModal}
              crossValidating={crossValidating}
              setCrossValidating={setCrossValidating}
              crossValidateText={crossValidateText}
              setCrossValidateText={setCrossValidateText}
              crossValidateResult={crossValidateResult}
              setCrossValidateResult={setCrossValidateResult}
              pollMergedTranscript={pollMergedTranscript}
              speakerMap={speakerMap}
              speakerStats={speakerStats}
              speakerColorMap={speakerColorMap}
              speakerStatsBySource={speakerStatsBySource}
              sourcesPresentes={sourcesPresentes}
              segmentsBySource={segmentsBySource}
              seuilDivergencePct={seuilDivergencePct}
              onSeuilChange={canEditKnowledge ? enregistrerSeuil : undefined}
              speakerLegendCollapsed={speakerLegendCollapsed}
              setSpeakerLegendCollapsed={setSpeakerLegendCollapsed}
              speakerOptions={speakerOptions}
              reloadSpeakerData={loadSpeakerData}
              setShowHistory={setShowHistory}
              loadRevisions={loadRevisions}
              loadVideoData={loadVideoData}
              setUrlProposals={setUrlProposals}
              urlValidating={urlValidating}
              setUrlValidating={setUrlValidating}
              startPolling={startPolling}
              useDiarisation={useDiarisation}
              useFacialId={useFacialId}
              videoDuration={duration || 0}
              transcriptContainerRef={transcriptContainerRef}
              data={data}
              activeIdx={activeIdx}
              seekTo={seekTo}
              activeSegmentRef={activeSegmentRef}
              highlightText={highlightText}
              isProcessing={isProcessing}
              formatTimeBracket={formatTimeBracket}
              currentTime={currentTime}
              parseTextTranscriptToSegments={parseTextTranscriptToSegments}
              urlProposals={urlProposals}
              urlApplying={urlApplying}
              urlDropdownSelections={urlDropdownSelections}
              punctuationLoading={punctuationLoading}
              punctuationProposal={punctuationProposal}
              rejectedCorrections={rejectedCorrections}
              editingCorrection={editingCorrection}
              bulkMinorBusy={bulkMinorBusy}
              setUrlApplying={setUrlApplying}
              setUrlDropdownSelections={setUrlDropdownSelections}
              setPunctuationLoading={setPunctuationLoading}
              setPunctuationProposal={setPunctuationProposal}
              setAcceptedPunctMarks={setAcceptedPunctMarks}
              setRejectedPunctMarks={setRejectedPunctMarks}
              setRejectedCorrections={setRejectedCorrections}
              setEditingCorrection={setEditingCorrection}
              setBulkMinorBusy={setBulkMinorBusy}
            />


            {/* ── Q&R Panel (Vincent 2026-05-05 — vrais sous-onglets Générer / Rapport) ── */}
            {/* Vincent B690 (Eric 2026-02-08) — Panneau Synthèse : entre Transcriptions et Q&R.
                Une seule synthèse par doc, avec boutons Générer/Régénérer/Éditer/Supprimer. */}
            <SynthesisPanel
              show={activePanel === "synthesis"}
              lang={lang}
              api={API}
              videoId={videoId}
              canEditKnowledge={canEditKnowledge}
              canManageRevisions={!!user?.is_founder || user?.role === "admin"}
              synthesis={(knowledge_units || []).find((u) => u.type === "synthesis") || null}
              /* Tâche 1331 — Marque verte du bouton « Générer dictionnaire »,
                 effacée en base dès qu'une nouvelle fusion est lancée. */
              dictionaryGeneratedAt={video?.dictionary_generated_at || null}
              onRefresh={loadVideoData}
              /* B1108 (Eric 2026-09-08) — Depuis un passage verrouillé, on
                 ouvre la transcription de la même vidéo avec le texte du
                 verrou déjà dans la recherche : corriger la source arrête la
                 répétition, alors que le verrou ne fait que la masquer. */
              onOpenTranscript={(lockedText) => {
                setActivePanel("transcript");
                setTranscriptSearch((lockedText || "").slice(0, 80));
                toast.info(tr(
                  "Transcription ouverte sur le passage verrouillé — corrigez la source ici.",
                  "Transcript opened on the locked passage — fix the source here.",
                ));
              }}
            />
            {/* ── Q&R Panel ──
                Eric 2026-06-25 (iter124 Session A8) — Extrait dans QAPanel.js
                (-484 lignes de VideoDetailPage.js). */}
            <QAPanel
              show={activePanel === "qa"}
              canEdit={canEditKnowledge}
              isAdmin={isAdmin}
              lang={lang}
              api={API}
              videoId={videoId}
              video={video}
              knowledgeUnits={knowledge_units}
              allTranscripts={allTranscripts}
              pipelineHasEverRun={pipelineHasEverRun}
              isTextDoc={isTextDoc}
              qaSubtab={qaSubtab}
              setQaSubtab={setQaSubtab}
              qaKeepPrevious={qaKeepPrevious}
              setQaKeepPrevious={setQaKeepPrevious}
              qaTitleAi={qaTitleAi}
              setQaClearHistoryConfirm={setQaClearHistoryConfirm}
              setQaHistoryOpenForKuId={setQaHistoryOpenForKuId}
              qaHistoryOpenForKuId={qaHistoryOpenForKuId}
              editingInlineKuId={editingInlineKuId}
              setEditingInlineKuId={setEditingInlineKuId}
              inlineKuDraft={inlineKuDraft}
              setInlineKuDraft={setInlineKuDraft}
              handleQaGenerateTitle={handleQaGenerateTitle}
              setImportOpen={setImportOpen}
              setDetailModalKu={setDetailModalKu}
              aiJobProgress={aiJobProgress}
              setAiJobProgress={setAiJobProgress}
              loadVideoData={loadVideoData}
              openReportModal={openReportModal}
              seekTo={seekTo}
              pollAgentJob={pollAgentJob}
            />


            {/* ── Scripts Panel ── Eric 2026-06-25 (iter123 Session A5) —
                Extrait dans ScriptsPanel.js (-175 lignes de VideoDetailPage.js). */}
            <ScriptsPanel
              show={activePanel === "scripts"}
              canEdit={canEditKnowledge}
              isAdmin={isAdmin}
              lang={lang}
              api={API}
              videoId={videoId}
              knowledgeUnits={knowledge_units}
              scriptsCount={data?.scripts_count}
              scriptsList={scriptsListForModal}
              allTranscripts={allTranscripts}
              pipelineHasEverRun={pipelineHasEverRun}
              scriptsSubtab={scriptsSubtab}
              setScriptsSubtab={setScriptsSubtab}
              scriptsKeepPrevious={scriptsKeepPrevious}
              setScriptsKeepPrevious={setScriptsKeepPrevious}
              importDocxLoading={importDocxLoading}
              importDocxFile={importDocxFile}
              loadVideoData={loadVideoData}
              openReportModal={openReportModal}
              seekTo={seekTo}
              onScriptCardClick={openScriptInModal}
              onScriptsListChange={setScriptsListForModal}
              scriptsReloadTick={scriptsReloadTick}
            />

            {/* ── Procédures Panel (Vincent 2026-05-05 — vrais sous-onglets) ── */}
            {/* ── Procédures Panel ──
                Eric 2026-06-25 (iter124 Session A7) — Extrait dans
                ProceduresPanel.js (-629 lignes de VideoDetailPage.js). */}
            <ProceduresPanel
              show={activePanel === "procedures"}
              canEdit={canEditKnowledge}
              isAdmin={isAdmin}
              lang={lang}
              api={API}
              videoId={videoId}
              video={video}
              knowledgeUnits={knowledge_units}
              allTranscripts={allTranscripts}
              pipelineHasEverRun={pipelineHasEverRun}
              proceduresSubtab={proceduresSubtab}
              setProceduresSubtab={setProceduresSubtab}
              proceduresKeepPrevious={proceduresKeepPrevious}
              setProceduresKeepPrevious={setProceduresKeepPrevious}
              procDraft={procDraft}
              setProcDraft={setProcDraft}
              procTitleProposals={procTitleProposals}
              setProcTitleProposals={setProcTitleProposals}
              procTitleHistory={procTitleHistory}
              setProcTitleHistory={setProcTitleHistory}
              procTitleGenerating={procTitleGenerating}
              setProcTitleGenerating={setProcTitleGenerating}
              setProcClearHistoryConfirm={setProcClearHistoryConfirm}
              editingProcedureId={editingProcedureId}
              setEditingProcedureId={setEditingProcedureId}
              notebookProcedureDoc={notebookProcedureDoc}
              setNotebookProcedureDoc={setNotebookProcedureDoc}
              notebookProcRunning={notebookProcRunning}
              setNotebookProcRunning={setNotebookProcRunning}
              importDocxLoading={importDocxLoading}
              importDocxFile={importDocxFile}
              setDetailModalKu={setDetailModalKu}
              loadVideoData={loadVideoData}
              openReportModal={openReportModal}
              seekTo={seekTo}
            />


            {/* Vincent XLSX 2026-07-09 15h20 Row #8 (Eric 2026-07-09 SPEC STRICK) —
                Onglet Objections enrichi : chaque tuile a désormais les MÊMES
                boutons d'action que Q&R / Concepts / Procédures (Accepter /
                Refuser / Éditer / Historique via KnowledgeDetailModal, +
                Effacer). Optimistic UI : badge Acceptée (vert) / Refusée
                (rouge) bascule instantanément au clic. Un clic sur la tuile
                ouvre la modale KnowledgeDetailModal partagée. */}
            {activePanel === "objections" && (
              <div data-testid="objections-panel">
                {/* Vincent B839 (Eric 2026-02-14 SPEC STRICK PROD) — 2 sous-onglets :
                    Pipeline (bandeau + boutons + message si 0) / Modifier ou Visualiser (liste).
                    Vincent B848 (Eric 2026-02-14 SPEC STRICK PROD) — Le bouton
                    Pipeline est masqué pour les non-éditeurs : ils n'ont aucun
                    bouton dans ce sous-onglet et ne devraient donc pas y accéder. */}
                <div className="flex border-b border-zinc-200 bg-zinc-50">
                  {canEditKnowledge && (
                    <button
                      onClick={() => setObjectionsSubtab("pipeline")}
                      className={`flex-1 py-2 text-xs font-medium transition-colors relative ${
                        objectionsSubtab === "pipeline" ? "text-red-700 bg-white" : "text-zinc-500 hover:text-zinc-700"
                      }`}
                      data-testid="objections-subtab-pipeline"
                    >
                      ⚗️ {lang === "fr" ? "Pipeline" : "Pipeline"}
                      {objectionsSubtab === "pipeline" && <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-red-500 rounded-full" />}
                    </button>
                  )}
                  <button
                    onClick={() => setObjectionsSubtab(canEditKnowledge ? "edit" : "view")}
                    className={`flex-1 py-2 text-xs font-medium transition-colors relative ${
                      (objectionsSubtab === "edit" || objectionsSubtab === "view") ? "text-red-700 bg-white" : "text-zinc-500 hover:text-zinc-700"
                    }`}
                    data-testid={canEditKnowledge ? "objections-subtab-edit" : "objections-subtab-view"}
                  >
                    {canEditKnowledge
                      ? <>📋 {lang === "fr" ? "Liste des Objections" : "Objections list"}</>
                      : <>👁 {lang === "fr" ? "Visualiser" : "View"}</>}
                    {(objectionsSubtab === "edit" || objectionsSubtab === "view") && <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-red-500 rounded-full" />}
                  </button>
                </div>
                {objectionsSubtab === "pipeline" && (<>
                {/* Vincent XLSX 2026-07-13 21h Row 46213 (Eric 2026-07-14 SPEC STRICK) —
                    Bandeau statut + boutons "Générer / Régénérer" + "Effacer"
                    au-dessus de la liste des objections, MÊME STRUCTURE que
                    l'onglet Concepts (parité stricte demandée par Vincent).
                    Endpoint : POST /api/videos/{id}/generate-scripts (qui
                    génère aussi les objections/réfutations en side-effect).
                    Effacer : DELETE knowledge-units/by-video?type=objection
                    + DELETE ?type=rebuttal en séquence. */}
                {isAdmin && (() => {
                  const objectionUnits = (knowledge_units || []).filter(u => u.type === "objection" || u.type === "rebuttal");
                  const objectionsTotal = objectionUnits.length;
                  const objectionsApproved = objectionUnits.filter(u => u.status === "approved").length;
                  const objectionsPending = objectionsTotal - objectionsApproved;
                  // Enhancement B614.d (Eric 2026-07-30) — Compteur ♥ favoris.
                  const objectionsFavorites = objectionUnits.filter(u => favKuIds.has(u.id)).length;
                  const objHasRun = objectionsTotal > 0;
                  // Vincent XLSX 2026-07-15 Row 485 — Masquer « Pipeline non lancé » dès que
                  // le pipeline a été lancé au moins une fois (même si 0 objections).
                  const showPipelineNonLance = !objHasRun && !pipelineHasEverRun;
                  return (
                    <>
                      <div className="px-4 py-2.5 bg-red-50/50 border-b border-red-200 flex items-center justify-between gap-3 text-[11px]" data-testid="objections-status-banner">
                        <div className="flex items-center gap-3">
                          <span className="font-bold text-red-800">{objectionsTotal} {lang === "fr" ? "objection(s)/réfutation(s)" : "objection(s)/rebuttal(s)"}</span>
                          <span className="text-red-700">✓ {objectionsApproved} {lang === "fr" ? "révisée(s)" : "reviewed"}</span>
                          {objectionsPending > 0 && <span className="text-amber-700">⏳ {objectionsPending} {lang === "fr" ? "à réviser" : "pending"}</span>}
                          {objectionsFavorites > 0 && (
                            <span className="text-rose-700" data-testid="objections-favorites-count">♥ {objectionsFavorites} {lang === "fr" ? (objectionsFavorites > 1 ? "favoris" : "favori") : (objectionsFavorites > 1 ? "favorites" : "favorite")}</span>
                          )}
                        </div>
                        {showPipelineNonLance && (
                          <span className="text-red-700/80 italic">{lang === "fr" ? "Pipeline non lancé" : "Pipeline not run"}</span>
                        )}
                      </div>
                      <div className="px-4 pt-4">
                        <div className="rounded-lg border border-red-200 bg-red-50/60 p-3" data-testid="objections-pipeline-box">
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              onClick={async () => {
                                if (genObjectionsLoading) return;
                                if (objHasRun) {
                                  const ok = await confirmDialog(lang === "fr"
                                    ? `Régénérer ajoutera de nouvelles objections/réfutations sans effacer les ${objectionsTotal} existantes.\n\nConseil : clique d'abord « Effacer » si tu veux repartir à zéro.\n\nContinuer ?`
                                    : `Re-running will add new objections/rebuttals on top of existing ${objectionsTotal}. Clear first if you want a fresh start. Continue?`);
                                  if (!ok) return;
                                }
                                setGenObjectionsLoading(true);
                                try {
                                  // Vincent XLSX 2026-07-15 Row 489 (Eric 2026-07-16 SPEC STRICK 2e passe) —
                                  // Endpoint DÉDIÉ /generate-objections : ne régénère PLUS les Q&R
                                  // ni les scripts. Utilise les scripts déjà en base. La barre
                                  // de progression affiche « Génération des objections » (pas Q&R).
                                  setAiJobProgress({ kind: "objections", chunks_processed: 0, total_chunks: 0, step: "starting" });
                                  const start = await axios.post(
                                    `${API}/api/videos/${videoId}/generate-objections`,
                                    {},
                                    { withCredentials: true, timeout: 60000 }
                                  );
                                  setAiJobProgress(null);
                                  const objCreated = start?.data?.objections_created ?? 0;
                                  const scrProcessed = start?.data?.scripts_processed ?? 0;
                                  toast.success(
                                    lang === "fr"
                                      ? `Extraction terminée — ${objCreated} objection(s) créée(s) à partir de ${scrProcessed} script(s), à valider dans cet onglet.`
                                      : `Extraction complete — ${objCreated} objection(s) created from ${scrProcessed} script(s)`
                                  );
                                  loadVideoData();
                                } catch (e) {
                                  setAiJobProgress(null);
                                  const detail = e.response?.data?.detail || e.message || "Erreur inconnue";
                                  toast.error(
                                    lang === "fr"
                                      ? `Erreur extraction objections : ${detail}`
                                      : `Objection extraction failed: ${detail}`,
                                    { duration: 8000 }
                                  );
                                } finally {
                                  setGenObjectionsLoading(false);
                                }
                              }}
                              disabled={genObjectionsLoading}
                              className="px-3 py-2.5 rounded-lg border-2 border-red-300 bg-white hover:bg-red-100 text-red-800 transition-all text-xs font-medium leading-tight disabled:opacity-50 disabled:cursor-not-allowed"
                              data-testid="objections-pipeline-launch-btn"
                            >
                              {genObjectionsLoading
                                ? (lang === "fr" ? "⏳ Extraction…" : "⏳ Extracting…")
                                : (lang === "fr"
                                  ? (objHasRun ? "🛑 Générer à nouveau les objections" : "🛑 Générer les objections")
                                  : (objHasRun ? "🛑 Generate objections again" : "🛑 Generate objections"))}
                            </button>
                            <button
                              onClick={async () => {
                                if (!objHasRun || genObjectionsLoading) return;
                                const ok = await confirmDialog(lang === "fr"
                                  ? `Effacer définitivement les ${objectionsTotal} objection(s)/réfutation(s) existante(s) ?\n\nCette action est irréversible.`
                                  : `Permanently delete the ${objectionsTotal} existing objection(s)/rebuttal(s)? This cannot be undone.`);
                                if (!ok) return;
                                setGenObjectionsLoading(true);
                                try {
                                  const [r1, r2] = await Promise.all([
                                    axios.delete(`${API}/api/agent/knowledge-units/by-video/${videoId}?type=objection`, { withCredentials: true }),
                                    axios.delete(`${API}/api/agent/knowledge-units/by-video/${videoId}?type=rebuttal`, { withCredentials: true }),
                                  ]);
                                  const total = (r1?.data?.count || 0) + (r2?.data?.count || 0);
                                  toast.success(
                                    lang === "fr"
                                      ? `${total} objection${total > 1 ? "s" : ""}/réfutation${total > 1 ? "s" : ""} effacée(s)`
                                      : `${total} objection(s)/rebuttal(s) cleared`
                                  );
                                  loadVideoData();
                                } catch (e) {
                                  toast.error(e.response?.data?.detail
                                    || tr("Erreur suppression objections", "Could not delete the objections"));
                                } finally {
                                  setGenObjectionsLoading(false);
                                }
                              }}
                              disabled={!objHasRun || genObjectionsLoading}
                              className="px-3 py-2.5 rounded-lg border-2 border-red-300 bg-white hover:bg-red-100 text-red-700 transition-all text-xs font-medium leading-tight disabled:opacity-40 disabled:cursor-not-allowed"
                              data-testid="objections-pipeline-clear-btn"
                              title={objHasRun
                                ? (lang === "fr" ? "Effacer toutes les objections existantes" : "Clear all existing objections")
                                : (lang === "fr" ? "Aucune objection à effacer" : "Nothing to clear")}
                            >
                              🗑 {lang === "fr" ? "Effacer les objections" : "Clear objections"}
                              {objHasRun && <span className="ml-1 opacity-70">({objectionsTotal})</span>}
                            </button>
                            {/* Tâche 1283 (Eric 2026-06-30) — « Nettoyer » :
                                repère les objections hors-sujet, orphelines ou
                                en doublon, les affiche avec leur raison, et
                                n'efface que ce qui est accepté. */}
                            <button
                              onClick={() => setObjectionsCleanupOpen(true)}
                              disabled={!objHasRun || genObjectionsLoading}
                              className="px-3 py-2.5 rounded-lg border-2 border-amber-300 bg-white hover:bg-amber-50 text-amber-800 transition-all text-xs font-medium leading-tight disabled:opacity-40 disabled:cursor-not-allowed"
                              data-testid="objections-cleanup-btn"
                              title={objHasRun
                                ? (lang === "fr"
                                    ? "Repérer les objections hors-sujet ou orphelines et choisir celles à supprimer"
                                    : "Find irrelevant or orphan objections and choose which ones to delete")
                                : (lang === "fr" ? "Aucune objection à nettoyer" : "Nothing to clean")}
                            >
                              🧹 {lang === "fr" ? "Nettoyer les objections" : "Clean objections"}
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  );
                })()}
                {/* Vincent B839 — Message vide dans sous-onglet Pipeline seulement */}
                {((knowledge_units || []).filter(u => u.type === "objection" || u.type === "rebuttal").length === 0) && (
                  <div className="text-center py-12 text-sm text-zinc-500 px-4" data-testid="objections-empty-pipeline">
                    {lang === "fr"
                      ? (canEditKnowledge
                          ? "Aucune objection identifiée dans cette vidéo pour le moment. Cliquez sur « Générer les objections » pour lancer l'extraction IA."
                          : "Aucune objection identifiée dans cette vidéo pour le moment.")
                      : (canEditKnowledge
                          ? "No objections identified in this video yet. Click \"Generate objections\" to run AI extraction."
                          : "No objections identified in this video yet.")}
                  </div>
                )}
                </>)}
                {(objectionsSubtab === "edit" || objectionsSubtab === "view") && (
                <div className="p-4 space-y-3">
                {(() => {
                  const objectionsAndRebuttals = (knowledge_units || [])
                    .filter(u => u.type === "objection" || u.type === "rebuttal")
                    .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
                  if (objectionsAndRebuttals.length === 0) {
                    // Vincent B838 (Eric 2026-02-14 SPEC STRICK PROD) —
                    // Pour les non-éditeurs, on retire le call-to-action
                    // « Cliquez sur 'Générer les objections' … » (le
                    // bouton ne leur est de toute façon pas visible).
                    return (
                      <div className="text-center py-12 text-sm text-zinc-500" data-testid="objections-empty">
                        {lang === "fr"
                          ? (canEditKnowledge
                              ? "Aucune objection identifiée dans cette vidéo pour le moment. Cliquez sur « Générer les objections » pour lancer l'extraction IA."
                              : "Aucune objection identifiée dans cette vidéo pour le moment.")
                          : (canEditKnowledge
                              ? "No objections identified in this video yet. Click \"Generate objections\" to run AI extraction."
                              : "No objections identified in this video yet.")}
                      </div>
                    );
                  }
                  return objectionsAndRebuttals.map((u) => {
                    const isObjection = u.type === "objection";
                    const timestamp = typeof u.timestamp === "number" ? u.timestamp : (u.timestamps?.start || 0);
                    const mm = String(Math.floor(timestamp / 60)).padStart(2, "0");
                    const ss = String(Math.floor(timestamp % 60)).padStart(2, "0");
                    const optStatus = objectionOptStatus[u.id];
                    const effectiveStatus = optStatus || u.status || "pending_review";
                    const isApproved = effectiveStatus === "approved";
                    const isRejected = effectiveStatus === "rejected";
                    const isPending = effectiveStatus === "pending_review";
                    return (
                      <div
                        key={u.id}
                        className={`rounded-lg border p-3 cursor-pointer transition-all hover:shadow-md ${isObjection ? "border-red-200 bg-red-50/40" : "border-teal-200 bg-teal-50/40"}`}
                        data-testid={`objection-card-${u.id}`}
                        data-focus-target={u.id}
                        onClick={() => setDetailModalKu(u)}
                      >
                        <div className="flex items-center justify-between mb-2 gap-2">
                          <div className="flex items-center gap-2">
                            {/* Vincent B614 (Eric 2026-07-30 SPEC STRICK) — ♥ favori
                                à gauche du badge type (parité stricte avec les
                                onglets Q&R, Scripts, Procédures, Concepts).
                                Le clic ne déclenche pas l'ouverture du modal
                                (stopPropagation dans KUFavoriteHeart). */}
                            <KUFavoriteHeart
                              unitId={u.id}
                              initialIsFavorite={favKuIds.has(u.id)}
                              testIdPrefix="fav-objection-btn"
                            />
                            <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${isObjection ? "bg-red-100 text-red-800" : "bg-teal-100 text-teal-800"}`}>
                              {isObjection ? (lang === "fr" ? "🛑 Objection" : "🛑 Objection") : (lang === "fr" ? "✓ Réfutation" : "✓ Rebuttal")}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => { e.stopPropagation(); seekTo && seekTo(timestamp); }}
                              className="text-[11px] font-mono text-[#002FA7] hover:underline"
                              data-testid={`objection-seek-${u.id}`}
                            >
                              {mm}:{ss}
                            </button>
                            {/* Badge statut */}
                            <span
                              className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                                isApproved ? "bg-teal-600 text-white" :
                                isRejected ? "bg-rose-600 text-white" :
                                "bg-amber-100 text-amber-700"
                              }`}
                              data-testid={`objection-status-${u.id}`}
                            >
                              {isApproved ? (lang === "fr" ? "Acceptée" : "Approved") :
                               isRejected ? (lang === "fr" ? "Refusée" : "Rejected") :
                               (lang === "fr" ? "En attente" : "Pending")}
                            </span>
                          </div>
                        </div>
                        {/* Vincent XLSX 2026-07-13 21h Row 46216 (Eric 2026-07-14 SPEC STRICK) —
                            Rendu structuré : Scénario / Réponse suggérée sur 2
                            champs distincts. Fini le dump JSON qui affichait
                            {"scenario":"…","response":"…"} en texte brut. */}
                        {(() => {
                          const scenario = u.content?.scenario || u.content?.text || u.content?.question || u.content?.script || u.content?.objection || "";
                          const response = u.content?.response || u.content?.rebuttal || u.content?.answer || "";
                          const displayScenario = typeof scenario === "string" ? scenario : JSON.stringify(scenario);
                          const displayResponse = typeof response === "string" ? response : JSON.stringify(response);
                          return (
                            <>
                              {displayScenario && (
                                <div className="text-sm text-zinc-800 whitespace-pre-wrap">
                                  <span className="text-[10px] font-semibold text-red-700 uppercase tracking-wide">
                                    {lang === "fr" ? "Objection du client : " : "Client objection: "}
                                  </span>
                                  {displayScenario}
                                </div>
                              )}
                              {displayResponse && (
                                <div className="mt-2 pt-2 border-t border-zinc-200 text-sm text-zinc-700 whitespace-pre-wrap">
                                  <span className="text-[10px] font-semibold text-teal-700 uppercase tracking-wide">
                                    {lang === "fr" ? "Réponse suggérée : " : "Suggested response: "}
                                  </span>
                                  {displayResponse}
                                </div>
                              )}
                              {!displayScenario && !displayResponse && u.content && (
                                <div className="text-xs text-zinc-400 italic">
                                  {lang === "fr" ? "Contenu vide — utilisez « Éditer » pour ajouter le scénario et la réponse." : "Empty content — use \"Edit\" to add scenario and response."}
                                </div>
                              )}
                            </>
                          );
                        })()}
                        {/* Vincent XLSX 2026-07-09 Row #8 — Bandeau d'action admin */}
                        {canEditKnowledge && (
                          <div className="mt-2 pt-2 border-t border-zinc-200 flex items-center gap-2 flex-wrap" data-testid={`objection-actions-${u.id}`}>
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (isApproved) return;
                                setObjectionOptStatus((prev) => ({ ...prev, [u.id]: "approved" }));
                                try {
                                  await axios.put(`${API}/api/knowledge/${u.id}/review`, { action: "approve" }, { withCredentials: true });
                                  toast.success(lang === "fr" ? "Objection acceptée" : "Objection approved");
                                  loadVideoData();
                                } catch (err) {
                                  setObjectionOptStatus((prev) => { const n = { ...prev }; delete n[u.id]; return n; });
                                  toast.error(err.response?.data?.detail || (lang === "fr" ? "Erreur" : "Error"));
                                }
                              }}
                              className={`px-2 py-1 text-[11px] rounded inline-flex items-center gap-1 font-semibold ${
                                isApproved
                                  ? "bg-teal-600 text-white ring-2 ring-emerald-300 cursor-default"
                                  : "border border-teal-300 text-teal-700 hover:bg-teal-50"
                              }`}
                              data-testid={`objection-approve-${u.id}`}
                              title={isApproved ? (lang === "fr" ? "État actuel : acceptée" : "Currently accepted") : (lang === "fr" ? "Accepter" : "Approve")}
                            >
                              ✓ {isApproved ? (lang === "fr" ? "Acceptée" : "Accepted") : (lang === "fr" ? "Accepter" : "Approve")}
                            </button>
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (isRejected) return;
                                const ok = await confirmDialog(lang === "fr" ? "Refuser cette objection ? Conservée pour audit (Loi 25)." : "Reject this objection?");
                                if (!ok) return;
                                setObjectionOptStatus((prev) => ({ ...prev, [u.id]: "rejected" }));
                                try {
                                  await axios.put(`${API}/api/knowledge/${u.id}/review`, { action: "reject" }, { withCredentials: true });
                                  toast.success(lang === "fr" ? "Objection refusée" : "Objection rejected");
                                  loadVideoData();
                                } catch (err) {
                                  setObjectionOptStatus((prev) => { const n = { ...prev }; delete n[u.id]; return n; });
                                  toast.error(err.response?.data?.detail || (lang === "fr" ? "Erreur" : "Error"));
                                }
                              }}
                              className={`px-2 py-1 text-[11px] rounded inline-flex items-center gap-1 font-semibold ${
                                isRejected
                                  ? "bg-rose-600 text-white ring-2 ring-rose-300 cursor-default"
                                  : "border border-rose-300 text-rose-700 hover:bg-rose-50"
                              }`}
                              data-testid={`objection-reject-${u.id}`}
                              title={isRejected ? (lang === "fr" ? "État actuel : refusée" : "Currently rejected") : (lang === "fr" ? "Refuser" : "Reject")}
                            >
                              ✗ {isRejected ? (lang === "fr" ? "Refusée" : "Rejected") : (lang === "fr" ? "Refuser" : "Reject")}
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); setDetailModalKu(u); }}
                              className="px-2 py-1 text-[11px] rounded border border-blue-300 text-blue-700 hover:bg-blue-50 inline-flex items-center gap-1"
                              data-testid={`objection-edit-${u.id}`}
                              title={lang === "fr" ? "Éditer / Historique" : "Edit / History"}
                            >
                              ✎ {lang === "fr" ? "Éditer" : "Edit"}
                            </button>
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                const ok = await confirmDialog(lang === "fr" ? "Effacer définitivement cette objection ?" : "Delete this objection permanently?");
                                if (!ok) return;
                                try {
                                  // Vincent XLSX 2026-07-09 Row #8 fix v2 — endpoint canonique
                                  // /api/agent/knowledge-units/{id} (soft-delete → Corbeille,
                                  // snapshot audit Loi 25). /api/knowledge/{id} DELETE n'existe pas.
                                  await axios.delete(`${API}/api/agent/knowledge-units/${u.id}`, { withCredentials: true });
                                  toast.success(lang === "fr" ? "Objection supprimée" : "Objection deleted");
                                  loadVideoData();
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || (lang === "fr" ? "Erreur" : "Error"));
                                }
                              }}
                              className="px-2 py-1 text-[11px] rounded border border-zinc-300 text-zinc-600 hover:bg-zinc-100 inline-flex items-center gap-1"
                              data-testid={`objection-delete-${u.id}`}
                              title={lang === "fr" ? "Effacer" : "Delete"}
                            >
                              🗑 {lang === "fr" ? "Effacer" : "Delete"}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
                </div>
                )}
                {/* Tâche 1283 — Revue des suppressions d'objections. */}
                <KnowledgeCleanupModal
                  open={objectionsCleanupOpen}
                  onClose={() => setObjectionsCleanupOpen(false)}
                  kind="objections"
                  api={API}
                  videoId={videoId}
                  lang={lang}
                  onApplied={() => loadVideoData()}
                />
              </div>
            )}

            {/* ── Knowledge Panel (Refacto P2 phase 3 — composant extrait) ── */}
            {activePanel === "knowledge" && (
              <div>
                {/* Vincent B839 (Eric 2026-02-14 SPEC STRICK PROD) — 2 sous-onglets :
                    Pipeline (bandeau + boutons + message si 0) / Modifier ou Visualiser (liste).
                    Vincent B848 (Eric 2026-02-14 SPEC STRICK PROD) — Bouton
                    Pipeline masqué pour non-éditeurs (parité stricte avec les
                    4 autres panneaux). */}
                <div className="flex border-b border-zinc-200 bg-zinc-50">
                  {canEditKnowledge && (
                    <button
                      onClick={() => setConceptsSubtab("pipeline")}
                      className={`flex-1 py-2 text-xs font-medium transition-colors relative ${
                        conceptsSubtab === "pipeline" ? "text-blue-700 bg-white" : "text-zinc-500 hover:text-zinc-700"
                      }`}
                      data-testid="concepts-subtab-pipeline"
                    >
                      ⚗️ {lang === "fr" ? "Pipeline" : "Pipeline"}
                      {conceptsSubtab === "pipeline" && <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-blue-500 rounded-full" />}
                    </button>
                  )}
                  <button
                    onClick={() => setConceptsSubtab(canEditKnowledge ? "edit" : "view")}
                    className={`flex-1 py-2 text-xs font-medium transition-colors relative ${
                      (conceptsSubtab === "edit" || conceptsSubtab === "view") ? "text-blue-700 bg-white" : "text-zinc-500 hover:text-zinc-700"
                    }`}
                    data-testid={canEditKnowledge ? "concepts-subtab-edit" : "concepts-subtab-view"}
                  >
                    {canEditKnowledge
                      ? <>📋 {lang === "fr" ? "Liste des Concepts" : "Concepts list"}</>
                      : <>👁 {lang === "fr" ? "Visualiser" : "View"}</>}
                    {(conceptsSubtab === "edit" || conceptsSubtab === "view") && <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-blue-500 rounded-full" />}
                  </button>
                </div>
                {conceptsSubtab === "pipeline" && (<>
                {/* Vincent #257 (2026-02-06) — Bandeau "Lancer" + "Effacer" pour
                    Concepts, cohérent avec Scripts/Procédures. Lance l'extraction
                    LLM et permet de vider les anciens avant régénération. */}
                {isAdmin && (() => {
                  const conceptsTotal = (knowledge_units || []).filter(u => u.type === "concept").length;
                  const conceptsApproved = (knowledge_units || []).filter(u => u.type === "concept" && u.status === "approved").length;
                  const conceptsPending = conceptsTotal - conceptsApproved;
                  // Enhancement B614.d (Eric 2026-07-30) — Compteur ♥ favoris.
                  const conceptsFavorites = (knowledge_units || []).filter(u => u.type === "concept" && favKuIds.has(u.id)).length;
                  const hasRun = conceptsTotal > 0;
                  return (
                    <>
                      <div className="px-4 py-2.5 bg-blue-50/50 border-b border-blue-200 flex items-center justify-between gap-3 text-[11px]" data-testid="concepts-status-banner">
                        <div className="flex items-center gap-3">
                          <span className="font-bold text-blue-800">{conceptsTotal} {lang === "fr" ? "concept(s)" : "concept(s)"}</span>
                          <span className="text-blue-700">✓ {conceptsApproved} {lang === "fr" ? "révisé(s)" : "reviewed"}</span>
                          {conceptsPending > 0 && <span className="text-amber-700">⏳ {conceptsPending} {lang === "fr" ? "à réviser" : "pending"}</span>}
                          {conceptsFavorites > 0 && (
                            <span className="text-rose-700" data-testid="concepts-favorites-count">♥ {conceptsFavorites} {lang === "fr" ? (conceptsFavorites > 1 ? "favoris" : "favori") : (conceptsFavorites > 1 ? "favorites" : "favorite")}</span>
                          )}
                        </div>
                        {!hasRun && (
                          <span className="text-blue-700/80 italic">{lang === "fr" ? "Pipeline non lancé" : "Pipeline not run"}</span>
                        )}
                      </div>
                      <div className="px-4 pt-4">
                        <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-3" data-testid="concepts-pipeline-box">
                          {/* Tâche 1237 (Eric 2026-06-26) — Sortie Word des
                              concepts : seules les Q&R pouvaient sortir en
                              .docx, il fallait tout recopier pour relire hors
                              de l'écran. */}
                          <button
                            onClick={() => window.open(
                              `${API}/api/videos/${videoId}/knowledge-report.docx?kind=concept`,
                              "_blank", "noopener")}
                            disabled={conceptsTotal === 0}
                            className="mb-2 w-full px-3 py-2.5 rounded-lg border-2 border-blue-300 bg-white hover:bg-blue-50 text-[#002FA7] transition-all text-xs font-medium leading-tight disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="concepts-report-docx-btn"
                            title={lang === "fr"
                              ? "Télécharger tous les concepts de cette vidéo dans un document Word"
                              : "Download every concept of this video as a Word document"}
                          >
                            📝 {lang === "fr" ? "Télécharger en Word" : "Download as Word"}
                          </button>
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              onClick={async () => {
                                if (genConceptsLoading) return;
                                if (hasRun) {
                                  const ok = await confirmDialog(lang === "fr"
                                    ? `Régénérer ajoutera ${conceptsTotal} nouveaux concepts sans effacer les anciens.\n\nConseil : clique d'abord « Effacer » si tu veux repartir à zéro.\n\nContinuer la régénération ?`
                                    : `Re-running will add new concepts on top of existing ${conceptsTotal}. Clear first if you want a fresh start. Continue?`);
                                  if (!ok) return;
                                }
                                setGenConceptsLoading(true);
                                try {
                                  // Eric (2026-06-12) — mode background + polling (anti-Cloudflare 100 s).
                                  // Eric (2026-06-14) — Barre de progression visuelle.
                                  setAiJobProgress({ kind: "concepts", chunks_processed: 0, total_chunks: 0, step: "starting" });
                                  const start = await axios.post(
                                    `${API}/api/agent/generate-concepts-batch/${videoId}?background=1`,
                                    {},
                                    { withCredentials: true, timeout: 30000 }
                                  );
                                  const data = await pollAgentJob(start.data.job_id, {
                                    onProgress: (p) => setJobProgressIfChanged("concepts", p),
                                  });
                                  setAiJobProgress(null);
                                  toast.success(
                                    lang === "fr"
                                      ? `${data.created} concept${data.created > 1 ? "s" : ""} extrait${data.created > 1 ? "s" : ""} — à valider`
                                      : `${data.created} concept(s) extracted — to validate`
                                  );
                                  loadVideoData();
                                } catch (e) {
                                  setAiJobProgress(null);
                                  toast.error(e.response?.data?.detail
                                    || tr("Erreur extraction concepts", "Could not extract the concepts"));
                                } finally {
                                  setGenConceptsLoading(false);
                                }
                              }}
                              disabled={genConceptsLoading}
                              className="px-3 py-2.5 rounded-lg border-2 border-blue-300 bg-white hover:bg-blue-100 text-blue-800 transition-all text-xs font-medium leading-tight disabled:opacity-50 disabled:cursor-not-allowed"
                              data-testid="concepts-pipeline-launch-btn"
                            >
                              {genConceptsLoading
                                ? (lang === "fr" ? "⏳ Extraction…" : "⏳ Extracting…")
                                : (lang === "fr"
                                  ? (hasRun ? "🧠 Générer à nouveau les concepts" : "🧠 Générer les concepts")
                                  : (hasRun ? "🧠 Generate concepts again" : "🧠 Generate concepts"))}
                            </button>
                            <button
                              onClick={async () => {
                                if (!hasRun || genConceptsLoading) return;
                                const ok = await confirmDialog(lang === "fr"
                                  ? `Effacer définitivement les ${conceptsTotal} concept${conceptsTotal > 1 ? "s" : ""} existant${conceptsTotal > 1 ? "s" : ""} pour cette vidéo ?\n\nCette action est irréversible.`
                                  : `Permanently delete the ${conceptsTotal} existing concept(s) for this video? This cannot be undone.`);
                                if (!ok) return;
                                setGenConceptsLoading(true);
                                try {
                                  const { data } = await axios.delete(
                                    `${API}/api/agent/knowledge-units/by-video/${videoId}?type=concept`,
                                    { withCredentials: true }
                                  );
                                  toast.success(
                                    lang === "fr"
                                      ? `${data.count} concept${data.count > 1 ? "s" : ""} effacé${data.count > 1 ? "s" : ""}`
                                      : `${data.count} concept(s) cleared`
                                  );
                                  loadVideoData();
                                } catch (e) {
                                  toast.error(e.response?.data?.detail
                                    || tr("Erreur suppression concepts", "Could not delete the concepts"));
                                } finally {
                                  setGenConceptsLoading(false);
                                }
                              }}
                              disabled={!hasRun || genConceptsLoading}
                              className="px-3 py-2.5 rounded-lg border-2 border-red-300 bg-white hover:bg-red-100 text-red-700 transition-all text-xs font-medium leading-tight disabled:opacity-40 disabled:cursor-not-allowed"
                              data-testid="concepts-pipeline-clear-btn"
                              title={hasRun
                                ? (lang === "fr" ? "Effacer tous les concepts existants" : "Clear all existing concepts")
                                : (lang === "fr" ? "Aucun concept à effacer" : "Nothing to clear")}
                            >
                              🗑 {lang === "fr" ? "Effacer les concepts" : "Clear concepts"}
                              {hasRun && <span className="ml-1 opacity-70">({conceptsTotal})</span>}
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  );
                })()}
                {/* Vincent B839 — message vide si aucun concept, dans sous-onglet Pipeline */}
                {((knowledge_units || []).filter(u => u.type === "concept").length === 0) && (
                  <div className="text-center py-12 text-sm text-zinc-500 px-4" data-testid="concepts-empty-pipeline">
                    {lang === "fr"
                      ? (isAdmin
                          ? "Aucun concept identifié dans cette vidéo pour le moment. Cliquez sur « Générer les concepts » pour lancer l'extraction IA."
                          : "Aucun concept identifié dans cette vidéo pour le moment.")
                      : (isAdmin
                          ? "No concepts identified in this video yet. Click \"Generate concepts\" to run AI extraction."
                          : "No concepts identified in this video yet.")}
                  </div>
                )}
                </>)}
                {(conceptsSubtab === "edit" || conceptsSubtab === "view") && (
                <KnowledgeTab
                  knowledgeUnits={knowledge_units}
                  isProcessing={isProcessing}
                  typeIcons={TYPE_ICONS}
                  typeColors={TYPE_COLORS}
                  formatTime={formatTime}
                  seekTo={seekTo}
                  lang={lang}
                  noKnowledgeYetLabel={t("no_knowledge_yet")}
                  isAdmin={isAdmin}
                  onEditItem={(unit) => setDetailModalKu(unit)}
                  onItemUpdated={() => loadVideoData()}
                />
                )}
              </div>
            )}

            {/* ── Speakers Panel ──
                Eric 2026-06-25 (iter121 Session A3) — Extrait dans SpeakersPanel.js
                pour réduire la taille de VideoDetailPage.js (-160 lignes). */}
            <SpeakersPanel
              show={activePanel === "speakers"}
              canEdit={canEditKnowledge}
              lang={lang}
              api={API}
              video={video}
              speakerData={speakerData}
              speakerMapping={speakerMapping}
              setSpeakerMapping={setSpeakerMapping}
              speakerSaving={speakerSaving}
              speakerReprocessing={speakerReprocessing}
              handleValidateSpeakers={handleValidateSpeakers}
              handleReprocessSpeakers={handleReprocessSpeakers}
              presentSpeakers={presentSpeakers}
              setPresentSpeakers={setPresentSpeakers}
              useDiarisation={useDiarisation}
              setUseDiarisation={setUseDiarisation}
              useFacialId={useFacialId}
              setUseFacialId={setUseFacialId}
              isFounder={!!user?.is_founder}
              isAdmin={isAdmin}
              videoId={videoId}
              loadVideoData={loadVideoData}
            />

          </div>
        </div>
      </div>

      {/* ── Modals (Share + History + Shortcuts + Report + Punctuation +
            QA/Proc clear history + KnowledgeDetail + Import + Delete) ──
            Eric 2026-06-25 (iter125 Session A10) — Tous les modals
            regroupés dans VideoDetailModals.js (-550 lignes). */}
      <VideoDetailModals
        lang={lang}
        api={API}
        video={video}
        videoId={videoId}
        isAdmin={isAdmin}
        navigate={navigate}
        showShareModal={showShareModal}
        setShowShareModal={setShowShareModal}
        shareLinks={shareLinks}
        shareCopied={shareCopied}
        shareLoading={shareLoading}
        shareExpiry={shareExpiry}
        setShareExpiry={setShareExpiry}
        createShareLink={createShareLink}
        copyShareLink={copyShareLink}
        revokeShareLink={revokeShareLink}
        showHistory={showHistory}
        setShowHistory={setShowHistory}
        revisions={revisions}
        revisionsLoading={revisionsLoading}
        restoring={restoring}
        handleRestoreRevision={handleRestoreRevision}
        showShortcutsHelp={showShortcutsHelp}
        setShowShortcutsHelp={setShowShortcutsHelp}
        reportModal={reportModal}
        setReportModal={setReportModal}
        downloadReportMarkdown={downloadReportMarkdown}
        downloadReportPDF={downloadReportPDF}
        punctuationProposal={punctuationProposal}
        setPunctuationProposal={setPunctuationProposal}
        punctuationApplying={punctuationApplying}
        setPunctuationApplying={setPunctuationApplying}
        rejectedPunctMarks={rejectedPunctMarks}
        setRejectedPunctMarks={setRejectedPunctMarks}
        acceptedPunctMarks={acceptedPunctMarks}
        setAcceptedPunctMarks={setAcceptedPunctMarks}
        loadAllTranscripts={loadAllTranscripts}
        loadVideoData={loadVideoData}
        qaClearHistoryConfirm={qaClearHistoryConfirm}
        setQaClearHistoryConfirm={setQaClearHistoryConfirm}
        qaTitleAi={qaTitleAi}
        handleQaClearHistoryConfirmed={handleQaClearHistoryConfirmed}
        detailModalKu={detailModalKu}
        setDetailModalKu={setDetailModalKu}
        detailModalNavPrev={detailModalNav.prev}
        detailModalNavNext={detailModalNav.next}
        detailModalNavLabel={detailModalNav.label}
        onScriptUpdated={() => setScriptsReloadTick((t) => t + 1)}
        procClearHistoryConfirm={procClearHistoryConfirm}
        setProcClearHistoryConfirm={setProcClearHistoryConfirm}
        setProcTitleHistory={setProcTitleHistory}
        importOpen={importOpen}
        setImportOpen={setImportOpen}
        showDeleteConfirm={showDeleteConfirm}
        setShowDeleteConfirm={setShowDeleteConfirm}
        deleteConfirmText={deleteConfirmText}
        setDeleteConfirmText={setDeleteConfirmText}
        deleting={deleting}
        setDeleting={setDeleting}
      />

      {/* Vincent B923 (Eric 2026-02-14 SPEC STRICK PROD) — Dialog de
          gestion des étiquettes (ajout manuel + suggestion auto).
          Ouvert au clic sur la chip « ÉTIQUETTES » du header. */}
      <TagsManagerDialog
        open={tagsManagerOpen}
        onOpenChange={setTagsManagerOpen}
        videoId={videoId}
        currentTags={Array.isArray(video?.tags) ? video.tags : []}
        onTagsChanged={() => { loadVideoData(); }}
        lang={lang}
        canEdit={canEditKnowledge}
      />
    </div>
  );
}
